import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Search, Plus, Save, Edit2, Trash2, Check, X,
  ChevronDown, Filter, Printer, BookOpen, BookMarked,
  Building2, Users, Layers, School, Loader2,
  FileText, ArrowDown, ArrowUp, Lock, Unlock, RotateCcw,
  AlertTriangle, Info, GraduationCap, ArrowUpDown,
  BookPlus, Eye, Copy, UserCheck, CheckCircle2,
  LayoutList, Settings2, Sparkles, FileDown, Upload
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { specialtiesApi, classesApi, schoolApi, cyclesApi, levelsApi, subjectsApi, subjectSpecialtiesApi, apiService } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import ExcelJS from 'exceljs'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

// ==================== TYPES ====================

interface Section { id: number; code: string; label_fr: string; label_en: string; school_id?: number }
interface Cycle { id: number; section_id: number; code: string; label_fr: string; label_en: string; order: number }
interface SpecialtyGroup { id: number; section_id: number; cycle_id: number; code: string; label_fr: string; label_en: string }
interface Specialty { id: number; specialty_group_id: number; code: string; abbreviation: string; label_fr: string; label_en: string }
interface Level { id: number; cycle_id: number; code: string; label_fr: string; label_en: string; order: number }
interface ClassItem { id: number; level_id: number; specialty_id: number; code: string; abbreviation: string; name: string; capacity: number; status: string; level_name?: string; specialty_name?: string; specialty_group_name?: string; section_id?: number; cycle_id?: number; school_id?: number; academic_year_id?: number | null }
interface Subject { id: number; code: string; label_fr: string; label_en: string; abbreviation: string; group_name?: string; section_id?: number; specialty_id?: number; status: string; school_id: number; academic_year_id?: number; section_name?: string; specialty_name?: string; teacher_id?: number; teacher_name?: string; created_at?: string; updated_at?: string; created_by?: number; updated_by?: number }
interface SubjectSpecialty { id: number; subject_id: number; specialty_id: number; cycle_id: number; level_id?: number; coefficient: number; hours_per_week: number; status: string; school_id?: number; academic_year_id?: number; created_at?: string; updated_at?: string; subject_code?: string; subject_label_fr?: string; subject_label_en?: string; specialty_name?: string; specialty_code?: string; specialty_group_name?: string; level_code?: string; level_name_fr?: string; level_name_en?: string }
interface SubjectCompetency { id: number; subject_specialty_id: number; trimester: number; number: number; label_fr: string; label_en?: string; abbreviation?: string; description?: string; status: string; school_id?: number; academic_year_id?: number; subject_name?: string; subject_code?: string; specialty_name?: string; level_name?: string }
interface AcademicYear { id: number; school_id: number; name: string; start_date: string; end_date: string; is_current: boolean; is_selected?: boolean }

type SortField = 'code' | 'label_fr' | 'label_en' | 'abbreviation' | 'group_name' | 'status' | 'section_name' | 'specialty_name' | 'created_at'
type SortDir = 'asc' | 'desc'

function cn(...classes: (string | false | null | undefined)[]) { return classes.filter(Boolean).join(' ') }

// ==================== HELPERS ====================

function getSectionSubsystem(section: Section): 'francophone' | 'anglophone' | null {
  const label = (section.label_fr || section.label_en || '').toLowerCase()
  if (label.includes('franc') || label.includes('français') || label.includes('bilingue')) return 'francophone'
  if (label.includes('anglais') || label.includes('anglophone') || label.includes('english')) return 'anglophone'
  if (section.id <= 2) return 'francophone'
  return 'anglophone'
}

function getClassSchoolId(cls: ClassItem, levels: Level[], cycles: Cycle[], sections: Section[]): number | undefined {
  const level = levels.find(l => l.id === cls.level_id)
  const cycle = level ? cycles.find(c => c.id === level.cycle_id) : undefined
  const section = cycle ? sections.find(s => s.id === cycle.section_id) : undefined
  return section?.school_id
}

function escapeCSV(value: string): string {
  const str = String(value ?? '')
  if (/[,\n\r"]/.test(str)) {
    return '"' + str.replace(/"/g, '""') + '"'
  }
  return str
}

function getFormattedDate(): string {
  return new Date().toISOString().split('T')[0]
}

function isFirstCycleLabel(label: string): boolean {
  const l = label.toLowerCase()
  return l.includes('1er') || l.includes('premier') || l.includes('first') || l.includes('1st') || l.includes('i ') || l.includes('ième')
}

function isSecondCycleLabel(label: string): boolean {
  const l = label.toLowerCase()
  return l.includes('2ème') || l.includes('second') || l.includes('2nd') || l.includes('deuxième') || l.includes('ii') || l.includes('secondaire')
}

// ==================== COMPONENT ====================

export default function LevelsAndClassesPage() {
  const { lang } = useLang()
  const { user, isAdmin, isSuperAdmin, hasPermission } = useAuthStore()

  const canManageSubjects = isSuperAdmin || isAdmin || user?.role === 'proviseur' || hasPermission('subjects.manage')
  const userSchoolId = user?.school_id || null

  // ============================================================================
  // STATE
  // ============================================================================

  const [sections, setSections] = useState<Section[]>([])
  const [cycles, setCycles] = useState<Cycle[]>([])
  const [specialtyGroups, setSpecialtyGroups] = useState<SpecialtyGroup[]>([])
  const [specialties, setSpecialties] = useState<Specialty[]>([])
  const [levels, setLevels] = useState<Level[]>([])
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [subjectSpecialties, setSubjectSpecialties] = useState<SubjectSpecialty[]>([])
  const [competencies, setCompetencies] = useState<SubjectCompetency[]>([])
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([])
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<number | ''>('')
  const [selectedSection, setSelectedSection] = useState<number | ''>('')
  const [selectedCycle, setSelectedCycle] = useState<number | ''>('')
  const [selectedSpecialty, setSelectedSpecialty] = useState<number | ''>('')
  const [selectedLevel, setSelectedLevel] = useState<number | ''>('')
  const [className, setClassName] = useState('')
  const [classAbbrev, setClassAbbrev] = useState('')
  const [isPermanentClass, setIsPermanentClass] = useState(true)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(true)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editAbbrev, setEditAbbrev] = useState('')
  const [editSpecialty, setEditSpecialty] = useState<number | ''>('')
  const [editLevel, setEditLevel] = useState<number | ''>('')

  // Tabs
  const [activeTab, setActiveTab] = useState<'classes' | 'cycles' | 'levels' | 'subjects' | 'subject-classes' | 'competencies'>('classes')

  // Cycle / Level forms
  const [cycleForm, setCycleForm] = useState({ code: '', label_fr: '', label_en: '', order: 1 })
  const [levelForm, setLevelForm] = useState({ code: '', label_fr: '', label_en: '', order: 1 })
  const [editingCycleId, setEditingCycleId] = useState<number | null>(null)
  const [editingLevelId, setEditingLevelId] = useState<number | null>(null)

  // Subject states
  const [subjectForm, setSubjectForm] = useState({ code: '', label_fr: '', label_en: '', abbreviation: '', group_name: '', section_id: '', specialty_id: '' })
  const [editingSubjectId, setEditingSubjectId] = useState<number | null>(null)
  const [subjectSearchTerm, setSubjectSearchTerm] = useState('')
  const [subjectSortField, setSubjectSortField] = useState<SortField>('label_fr')
  const [subjectSortDir, setSubjectSortDir] = useState<SortDir>('asc')
  const [subjectFilterStatus, setSubjectFilterStatus] = useState<string>('')
  const [subjectFilterSection, setSubjectFilterSection] = useState<number | ''>('')
  const [subjectFilterGroup, setSubjectFilterGroup] = useState<string>('')
  const [subjectGroups, setSubjectGroups] = useState<string[]>(() => {
    try { const saved = localStorage.getItem('lybibal-subject-groups'); if (saved) return JSON.parse(saved) } catch (e) { /* ignore */ }
    return ['MATIERES LITTERAIRES', 'MATIERES SCIENTIFIQUES', 'MATIERES CULTURELLES ET ARTISTIQUES', 'MATIERES GENERALES']
  })
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [teachers, setTeachers] = useState<{id: number; first_name: string; last_name: string; full_name?: string}[]>([])
  const [apModalOpen, setApModalOpen] = useState(false)
  const [apSubjectId, setApSubjectId] = useState<number | null>(null)
  const [apTeacherId, setApTeacherId] = useState<number | ''>('')

  // Subject-Class states
  const [scModalOpen, setScModalOpen] = useState(false)
  const [scModalMode, setScModalMode] = useState<'create' | 'edit' | 'bulk-cycle'>('create')
  const [scForm, setScForm] = useState({ subject_id: '', specialty_id: '', cycle_id: '', level_id: '', coefficient: '1', hours_per_week: '2', status: 'ACTIVE', subsystem: '' as 'francophone' | 'francophone-bilingual' | 'anglophone' | 'anglophone-bilingual' | '' })
  const [editingScId, setEditingScId] = useState<number | null>(null)
  const [scSearchTerm, setScSearchTerm] = useState('')
  const [scFilterSpecialty, setScFilterSpecialty] = useState<number | ''>('')
  const [scFilterSubject, setScFilterSubject] = useState<number | ''>('')
  const [scFilterLevel, setScFilterLevel] = useState<number | ''>('')

  // Competency states
  const [competencyModalOpen, setCompetencyModalOpen] = useState(false)
  const [competencyModalSubjectSpecialty, setCompetencyModalSubjectSpecialty] = useState<SubjectSpecialty | null>(null)
  const [competencyForm, setCompetencyForm] = useState<{trimester: number; number: number; label_fr: string; label_en: string; abbreviation: string; description: string}>({ trimester: 1, number: 1, label_fr: '', label_en: '', abbreviation: '', description: '' })
  const [editingCompetencyId, setEditingCompetencyId] = useState<number | null>(null)
  const [competencyFilterSection, setCompetencyFilterSection] = useState<number | ''>('')
  const [competencyFilterCycle, setCompetencyFilterCycle] = useState<number | ''>('')
  const [competencyFilterLevel, setCompetencyFilterLevel] = useState<number | ''>('')
  const [competencyFilterSpecialty, setCompetencyFilterSpecialty] = useState<number | ''>('')
  const [competencyFilterSubject, setCompetencyFilterSubject] = useState<number | ''>('')
  const [competencyPageSize, setCompetencyPageSize] = useState<number>(20)
  const [importingCompetencies, setImportingCompetencies] = useState(false)
  const [competencyCurrentPage, setCompetencyCurrentPage] = useState<number>(1)
  // Copie de competences entre specialites
  const [compCopyModalOpen, setCompCopyModalOpen] = useState(false)
  const [compCopySource, setCompCopySource] = useState<number | ''>('')
  const [compCopyTarget, setCompCopyTarget] = useState<number | ''>('')
  const [compCopyLevel, setCompCopyLevel] = useState<number | ''>('')
  const [compCopyOverwrite, setCompCopyOverwrite] = useState(false)
  const [modalCopySource, setModalCopySource] = useState<number | ''>('')
  const [isCopying, setIsCopying] = useState(false)
  // Suppression de masse des compétences
  const [confirmDeleteAllSubject, setConfirmDeleteAllSubject] = useState(false)
  const [deletingAllSubject, setDeletingAllSubject] = useState(false)
  const [confirmDeleteAllGlobal, setConfirmDeleteAllGlobal] = useState(false)
  const [deletingAllGlobal, setDeletingAllGlobal] = useState(false)

  // Duplicate states
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)
  const [duplicateSourceClass, setDuplicateSourceClass] = useState<number | ''>('')
  const [duplicateTargetClass, setDuplicateTargetClass] = useState<number | ''>('')
  const [duplicateOverwrite, setDuplicateOverwrite] = useState(false)

  // Modal states
  const [subjectModalOpen, setSubjectModalOpen] = useState(false)
  const [subjectModalMode, setSubjectModalMode] = useState<'create' | 'edit'>('create')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; type: 'subject' | 'cycle' | 'level' | 'class' | 'subject-class' | 'competency'; name: string } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const tableRef = useRef<HTMLDivElement>(null)
  const competencyFileInputRef = useRef<HTMLInputElement>(null)

  // ============================================================================
  // TRANSLATIONS
  // ============================================================================

  const t = {
    fr: {
      classes: 'Classes', cycles: 'Cycles', levels: 'Niveaux', subjects: 'Matieres', subjectClasses: 'Matieres par Specialite', competencies: 'Competences',
      total: 'Total', open: 'Ouvertes', closed: 'Fermees', levelsCount: 'Niveaux',
      subjectsCount: 'Matieres', search: 'Rechercher...', filters: 'Filtres', allCycles: 'Tous les cycles', allLevels: 'Tous les niveaux',
      incompleteStructure: 'Structure incomplete', createCyclesFirst: 'Veuillez d\'abord creer des cycles dans l\'onglet "Cycles".',
      createLevelsFirst: 'Veuillez d\'abord creer des niveaux dans l\'onglet "Niveaux".',
      noSections: 'Aucune section disponible', configureSections: "Veuillez d'abord configurer les sections dans les parametres de l'ecole.",
      noCycles: 'Aucun cycle disponible', createCyclesFirst2: 'Veuillez d\'abord creer des cycles dans l\'onglet "Cycles".',
      filterAndCreate: 'Filtrer et creer une classe', section: 'Section', cycle: 'Cycle',
      specialty: 'Specialite / Serie', level: 'Niveau', addClass: 'Ajouter Classe',
      className: 'Nom de la classe', classAbbrev: 'Abreviation', classList: 'Liste des classes',
      results: 'resultat(s)', excel: 'Excel', csv: 'CSV', pdf: 'PDF',
      noClasses: 'Aucune classe', useFilters: 'Utilisez les filtres ci-dessus pour creer une classe',
      createCycle: 'Creer un cycle', editCycle: 'Modifier le cycle', code: 'Code',
      labelFr: 'Libelle FR', labelEn: 'Libelle EN', addCycle: 'Ajouter Cycle',
      cycleList: 'Liste des cycles', noCycles2: 'Aucun cycle', createCycleStart: 'Creez un cycle pour commencer',
      createLevel: 'Creer un niveau', editLevel: 'Modifier le niveau', addLevel: 'Ajouter Niveau',
      levelList: 'Liste des niveaux', noLevels: 'Aucun niveau', createLevelStart: 'Creez un niveau pour commencer',
      save: 'Enregistrer', cancel: 'Annuler', noSpecialty: 'Aucune specialite pour ce cycle',
      noLevelsForCycle: 'Aucun niveau pour ce cycle. Creez-en un dans l\'onglet Niveaux.',
      noCyclesForSection: 'Aucun cycle pour cette section. Creez-en un dans l\'onglet Cycles.',
      select: 'Selectionner...', status: 'Statut', openStatus: 'Active', closedStatus: 'Inactive',
      edit: 'Modifier', delete: 'Supprimer', deleteConfirm: 'Supprimer cette classe ?',
      deleteCycleConfirm: 'Supprimer ce cycle ?', deleteLevelConfirm: 'Supprimer ce niveau ?',
      deleteSubjectConfirm: 'Supprimer cette matiere ?',
      deleteSubjectClassConfirm: 'Supprimer cette attribution ?',
      deleteCompetencyConfirm: 'Supprimer cette competence ?',
      loadingError: 'Erreur chargement', fillAllFields: 'Veuillez remplir tous les champs',
      classCreated: 'Classe creee', classUpdated: 'Modifie', classDeleted: 'Classe supprimee',
      cycleCreated: 'Cycle cree', cycleUpdated: 'Cycle modifie', cycleDeleted: 'Cycle supprime',
      levelCreated: 'Niveau cree', levelUpdated: 'Niveau modifie', levelDeleted: 'Niveau supprime',
      subjectCreated: 'Matiere creee', subjectUpdated: 'Matiere modifiee', subjectDeleted: 'Matiere supprimee',
      subjectClassCreated: 'Attribution creee', subjectClassUpdated: 'Attribution modifiee', subjectClassDeleted: 'Attribution supprimee',
      competencyCreated: 'Competence creee', competencyUpdated: 'Competence modifiee', competencyDeleted: 'Competence supprimee',
      statusUpdated: 'Statut mis a jour', createError: 'Erreur creation', updateError: 'Erreur modification',
      deleteError: 'Erreur suppression', noDataExport: 'Aucune donnee a exporter',
      csvExported: 'CSV exporte avec succes', excelExported: 'Excel exporte avec succes',
      pdfExported: 'PDF exporte avec succes', excelExportError: 'Erreur lors de l\'export Excel',
      generatedOn: 'Genere le', summary: 'Resume', totalClasses: 'Total classes',
      abbreviation: 'Abrev.', actions: 'Actions', section2: 'Section', cycle2: 'Cycle',
      specialty2: 'Specialite', level2: 'Niveau', class2: 'Classe',
      createSubject: 'Nouvelle matiere', editSubject: 'Modifier la matiere',
      addSubject: 'Ajouter', subjectList: 'Liste des matieres',
      noSubjects: 'Aucune matiere', createSubjectStart: 'Creez une matiere pour commencer',
      fillSubjectFields: 'Veuillez remplir tous les champs obligatoires',
      subjectCodeExists: 'Ce code existe deja',
      confirmDeleteTitle: 'Confirmer la suppression',
      confirmDeleteMessage: 'Etes-vous sur de vouloir supprimer',
      confirmDeleteWarning: 'Cette action est irreversible.',
      close: 'Fermer', confirm: 'Confirmer',
      modalCreateSubject: 'Creer une matiere', modalEditSubject: 'Modifier une matiere',
      subjectDetails: 'Details de la matiere',
      sortBy: 'Trier par', all: 'Tous', active: 'Actif', inactive: 'Inactif',
      groupName: 'Groupe de matiere',
      manageGroups: 'Gerer les groupes',
      addGroup: 'Ajouter un groupe',
      newGroup: 'Nouveau groupe',
      groupAdded: 'Groupe ajoute',
      groupDeleted: 'Groupe supprime',
      groupExists: 'Ce groupe existe deja',
      subjectClassList: 'Attributions Matiere-Classe',
      subjectClassExportTitle: 'Liste des matieres par niveau et specialite',
      noSubjectClasses: 'Aucune attribution',
      createSubjectClassStart: 'Attributer une matiere a une classe',
      assignSubject: 'Attribuer une matiere',
      assignByLevel: 'Attribuer par niveau',
      coefficient: 'Coefficient', hoursPerWeek: 'H/sem.',
      classFilter: 'Classe', subjectFilter: 'Matiere',
      permissionDenied: 'Permission refusee',
      readOnly: 'Lecture seule',
      manageSubjects: 'Gerer les matieres',
      manageAssignments: 'Gerer les attributions',
      allSchools: 'Tous les etablissements',
      filterBySchool: 'Filtrer par etablissement',
      academicYear: 'Annee scolaire',
      permanentClass: 'Classe permanente',
      permanentClassHelp: 'Les classes permanentes ne sont pas liees a une annee scolaire specifique et peuvent etre reutilisees chaque annee.',
      yearFilter: 'Filtrer par annee',
      allYears: 'Toutes les annees',
      assignToAllClasses: 'Attribuer a toutes les classes du niveau',
      bulkAssignmentSuccess: 'Attribution par niveau reussie',
      notCurrentYear: 'Operation impossible : l\'annee selectionnee n\'est pas l\'annee en cours de cet etablissement. Veuillez selectionner l\'annee active.',
      subjectSpecialtyCreated: 'Attribution creee',
      subjectSpecialtyUpdated: 'Attribution modifiee',
      subjectSpecialtyDeleted: 'Attribution supprimee',
      subsystem: 'Sous-systeme educatif',
      selectSubsystem: 'Selectionner...',
      francophoneSubsystem: 'Francophone',
      anglophoneSubsystem: 'Anglophone',
      generalSecondary: 'Enseignement Secondaire General',
      bilingualProgram: 'Programme d\'Education Bilingue Special',
      subsystemHelp: 'Filtre les cycles selon le sous-systeme educatif.',
      levelFilter: 'Niveau',
      // Competencies
      competencyConfig: 'Configuration des competences',
      competencyConfigDesc: 'Definissez les competences par trimestre pour chaque matiere, par NIVEAU et par SPECIALITE : 1er cycle (6e, 5e, 4e ALL/ESP, 3e ALL/ESP) ET Form 5 (5A Arts / 5B Sciences). Maximum 6 par trimestre.',
      trimester1: 'Trimestre 1',
      trimester2: 'Trimestre 2',
      trimester3: 'Trimestre 3',
      competencyNumber: 'Competence',
      competencyLabel: 'Libelle',
      competencyDesc: 'Description',
      addCompetency: 'Ajouter une competence',
      maxCompetencies: 'Maximum 6 competences par trimestre',
      noCompetencies: 'Aucune competence configuree',
      configureCompetencies: 'Configurer',
      noCompetencyRows: 'Aucune matiere pour ces filtres. Verifiez les attributions Matiere-Specialite (onglet "Matieres par Specialite").',
      competenciesConfigured: 'competence(s) configuree(s)',
      competencyExportTitle: 'Liste des competences par matiere, niveau et specialite',
      downloadTemplate: 'Telecharger le modele',
      importCompetencies: 'Importer un fichier',
      importingCompetencies: 'Import en cours...',
      importHint: 'Telechargez le modele (pre-rempli avec toutes les matieres), remplissez les libelles, puis uploadez : toutes les matieres sont enregistrees en une seule fois.',
      competencyImportSuccess: 'competence(s) importee(s) avec succes',
      competencyImportError: "Erreur lors de l'import",
      competencyImportEmpty: 'Aucune competence valide dans le fichier (Libelle FR requis, Code matiere valide, Trimestre/Numero entre 1 et 3)',
      competencyImportSkipped: 'deja existante(s) ignoree(s)',
      competencyImportUnknownCodes: 'Code(s) matiere inconnu(s)',
      competencyImportDuplicate: 'Doublons detectes : chaque couple Trimestre/Numero doit etre unique',
      templateTitle: 'Modele - Configuration des competences',
      templateInstruction: 'Une ligne = une competence. Colonnes Code matiere, Niveau, Trimestre et Numero deja pre-remplies : saisissez Abreviation, Libelle FR (obligatoire), Libelle EN et Description. Toutes les matieres de tous les niveaux et specialites sont dans le meme fichier.',
      subjectCompetencyTitle: 'Competences de la matiere',
      // Copie de competences
      copyCompetencies: 'Copier les competences',
      copyFromSpecialty: 'Copier depuis une specialite',
      copySubjectCompetencies: 'Copier les competences d\'une autre specialite vers celle-ci',
      copyHint: 'Ex: copier 4e Allemand -> 4e Espagnol, puis ne modifier que la LV2. Les couples Trimestre/Numero existants sont ignores (ou ecrases si coche).',
      sourceSpecialty: 'Specialite source',
      targetSpecialty: 'Specialite cible',
      copyLevel: 'Niveau (optionnel)',
      copySuccess: 'competence(s) copiee(s)',
      copySkipped: 'ignoree(s)',
      copyNoMatch: 'Aucune competence source trouvee',
      copyError: 'Erreur lors de la copie',
      noOtherSpecialty: 'Aucune autre specialite avec cette matiere',
      copy: 'Copier',
      overwriteExisting: 'Ecraser les existantes',
    },
    en: {
      classes: 'Classes', cycles: 'Cycles', levels: 'Levels', subjects: 'Subjects', subjectClasses: 'Subject Specialties', competencies: 'Competencies',
      total: 'Total', open: 'Open', closed: 'Closed', levelsCount: 'Levels',
      subjectsCount: 'Subjects', search: 'Search...', filters: 'Filters', allCycles: 'All cycles', allLevels: 'All levels',
      incompleteStructure: 'Incomplete structure', createCyclesFirst: 'Please create cycles first in the "Cycles" tab.',
      createLevelsFirst: 'Please create levels first in the "Levels" tab.',
      noSections: 'No sections available', configureSections: 'Please configure sections in the school settings first.',
      noCycles: 'No cycles available', createCyclesFirst2: 'Please create cycles first in the "Cycles" tab.',
      filterAndCreate: 'Filter and create a class', section: 'Section', cycle: 'Cycle',
      specialty: 'Specialty / Series', level: 'Level', addClass: 'Add Class',
      className: 'Class name', classAbbrev: 'Abbreviation', classList: 'Class list',
      results: 'result(s)', excel: 'Excel', csv: 'CSV', pdf: 'PDF',
      noClasses: 'No classes', useFilters: 'Use the filters above to create a class',
      createCycle: 'Create cycle', editCycle: 'Edit cycle', code: 'Code',
      labelFr: 'Label FR', labelEn: 'Label EN', addCycle: 'Add Cycle',
      cycleList: 'Cycle list', noCycles2: 'No cycles', createCycleStart: 'Create a cycle to get started',
      createLevel: 'Create level', editLevel: 'Edit level', addLevel: 'Add Level',
      levelList: 'Level list', noLevels: 'No levels', createLevelStart: 'Create a level to get started',
      save: 'Save', cancel: 'Cancel', noSpecialty: 'No specialties for this cycle',
      noLevelsForCycle: 'No levels for this cycle. Create one in the Levels tab.',
      noCyclesForSection: 'No cycles for this section. Create one in the Cycles tab.',
      select: 'Select...', status: 'Status', openStatus: 'Active', closedStatus: 'Inactive',
      edit: 'Edit', delete: 'Delete', deleteConfirm: 'Delete this class?',
      deleteCycleConfirm: 'Delete this cycle?', deleteLevelConfirm: 'Delete this level?',
      deleteSubjectConfirm: 'Delete this subject?',
      deleteSubjectClassConfirm: 'Delete this assignment?',
      deleteCompetencyConfirm: 'Delete this competency?',
      loadingError: 'Loading error', fillAllFields: 'Please fill all fields',
      classCreated: 'Class created', classUpdated: 'Updated', classDeleted: 'Class deleted',
      cycleCreated: 'Cycle created', cycleUpdated: 'Cycle updated', cycleDeleted: 'Cycle deleted',
      levelCreated: 'Level created', levelUpdated: 'Level updated', levelDeleted: 'Level deleted',
      subjectCreated: 'Subject created', subjectUpdated: 'Subject updated', subjectDeleted: 'Subject deleted',
      subjectClassCreated: 'Assignment created', subjectClassUpdated: 'Assignment updated', subjectClassDeleted: 'Assignment deleted',
      subjectSpecialtyCreated: 'Assignment created', subjectSpecialtyUpdated: 'Assignment updated', subjectSpecialtyDeleted: 'Assignment deleted',
      competencyCreated: 'Competency created', competencyUpdated: 'Competency updated', competencyDeleted: 'Competency deleted',
      statusUpdated: 'Status updated', createError: 'Create error', updateError: 'Update error',
      deleteError: 'Delete error', noDataExport: 'No data to export',
      csvExported: 'CSV exported successfully', excelExported: 'Excel exported successfully',
      pdfExported: 'PDF exported successfully', excelExportError: 'Error exporting Excel',
      generatedOn: 'Generated on', summary: 'Summary', totalClasses: 'Total classes',
      abbreviation: 'Abbrev.', actions: 'Actions', section2: 'Section', cycle2: 'Cycle',
      specialty2: 'Specialty', level2: 'Level', class2: 'Class',
      createSubject: 'New subject', editSubject: 'Edit subject',
      addSubject: 'Add', subjectList: 'Subject list',
      noSubjects: 'No subjects', createSubjectStart: 'Create a subject to get started',
      fillSubjectFields: 'Please fill all required fields',
      subjectCodeExists: 'This code already exists',
      confirmDeleteTitle: 'Confirm deletion',
      confirmDeleteMessage: 'Are you sure you want to delete',
      confirmDeleteWarning: 'This action cannot be undone.',
      close: 'Close', confirm: 'Confirm',
      modalCreateSubject: 'Create a subject', modalEditSubject: 'Edit a subject',
      subjectDetails: 'Subject details',
      sortBy: 'Sort by', all: 'All', active: 'Active', inactive: 'Inactive',
      groupName: 'Subject group',
      manageGroups: 'Manage groups',
      addGroup: 'Add a group',
      newGroup: 'New group',
      groupAdded: 'Group added',
      groupDeleted: 'Group deleted',
      groupExists: 'This group already exists',
      subjectClassList: 'Subject-Class Assignments',
      subjectClassExportTitle: 'Subject list by level and specialty',
      noSubjectClasses: 'No assignments',
      createSubjectClassStart: 'Assign a subject to a class',
      assignSubject: 'Assign subject',
      assignByLevel: 'Assign by level',
      coefficient: 'Coefficient', hoursPerWeek: 'H/week',
      classFilter: 'Class', subjectFilter: 'Subject',
      permissionDenied: 'Permission denied',
      readOnly: 'Read only',
      manageSubjects: 'Manage subjects',
      manageAssignments: 'Manage assignments',
      allSchools: 'All schools',
      filterBySchool: 'Filter by school',
      academicYear: 'Academic year',
      permanentClass: 'Permanent class',
      permanentClassHelp: 'Permanent classes are not tied to a specific academic year and can be reused every year.',
      yearFilter: 'Filter by year',
      allYears: 'All years',
      assignToAllClasses: 'Assign to all classes of the level',
      bulkAssignmentSuccess: 'Bulk assignment by level successful',
      notCurrentYear: 'Operation not allowed: the selected year is not the current year of this school. Please select the active year.',
      subsystem: 'Educational subsystem',
      selectSubsystem: 'Select...',
      francophoneSubsystem: 'Francophone',
      anglophoneSubsystem: 'Anglophone',
      generalSecondary: 'General Secondary Education',
      bilingualProgram: 'Special Bilingual Education Program',
      subsystemHelp: 'Filter cycles by educational subsystem.',
      levelFilter: 'Level',
      // Competencies
      competencyConfig: 'Competency Configuration',
      competencyConfigDesc: 'Define competencies by trimester for each subject, by LEVEL and by SPECIALTY: 1st cycle AND Form 5 (5A Arts / 5B Science). Maximum 6 per trimester.',
      trimester1: 'Trimester 1',
      trimester2: 'Trimester 2',
      trimester3: 'Trimester 3',
      competencyNumber: 'Competency',
      competencyLabel: 'Label',
      competencyDesc: 'Description',
      addCompetency: 'Add a competency',
      maxCompetencies: 'Maximum 6 competencies per trimester',
      noCompetencies: 'No competencies configured',
      configureCompetencies: 'Configure',
      noCompetencyRows: 'No subjects for these filters. Check Subject-Specialty assignments ("Subject Specialties" tab).',
      competenciesConfigured: 'competency(ies) configured',
      competencyExportTitle: 'Competency list by subject, level and specialty',
      downloadTemplate: 'Download template',
      importCompetencies: 'Import file',
      importingCompetencies: 'Importing...',
      importHint: 'Download the template (pre-filled with all subjects), fill in the labels, then upload: all subjects are saved in one go.',
      competencyImportSuccess: 'competency(ies) imported successfully',
      competencyImportError: 'Import error',
      competencyImportEmpty: 'No valid competency in the file (Label FR required, valid subject code, Trimester/Number between 1 and 3)',
      competencyImportSkipped: 'already existing skipped',
      competencyImportUnknownCodes: 'Unknown subject code(s)',
      competencyImportDuplicate: 'Duplicates detected: each Trimester/Number pair must be unique',
      templateTitle: 'Template - Competency configuration',
      templateInstruction: 'One row = one competency. Subject Code, Level, Trimester and Number columns are pre-filled: just type Abbreviation, Label FR (required), Label EN and Description. All subjects of all levels and specialties are in the same file.',
      subjectCompetencyTitle: 'Subject Competencies',
      // Competency copy
      copyCompetencies: 'Copy competencies',
      copyFromSpecialty: 'Copy from a specialty',
      copySubjectCompetencies: 'Copy competencies from another specialty into this one',
      copyHint: 'E.g. copy 4th German -> 4th Spanish, then only edit LV2. Existing Trimester/Number pairs are skipped (or overwritten if checked).',
      sourceSpecialty: 'Source specialty',
      targetSpecialty: 'Target specialty',
      copyLevel: 'Level (optional)',
      copySuccess: 'competency(ies) copied',
      copySkipped: 'skipped',
      copyNoMatch: 'No source competency found',
      copyError: 'Copy error',
      noOtherSpecialty: 'No other specialty with this subject',
      copy: 'Copy',
      overwriteExisting: 'Overwrite existing',
    }
  }
  const txt = t[lang] || t.fr

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadAllData = useCallback(async () => {
    setLoading(true)
    try {
      try {
        const userSchoolId = user?.school_id || 1
        const yearsData = await schoolApi.getAcademicYears ? await schoolApi.getAcademicYears(userSchoolId) : []
        const yearsArray = Array.isArray(yearsData) ? yearsData : (yearsData?.data || [])
        setAcademicYears(yearsArray)
      } catch (e) {
        console.warn('[AcademicYears] Failed to load:', e)
        setAcademicYears([])
      }

      const secData = await schoolApi.getSections(1)
      setSections(secData || [])

      const [cyclesData, levelsData, classesData] = await Promise.allSettled([
        cyclesApi.getAll(), levelsApi.getAll(), classesApi.getAll()
      ])
      setCycles(cyclesData.status === 'fulfilled' && (cyclesData.value as any)?.length > 0 ? (cyclesData.value as any) : [])
      setLevels(levelsData.status === 'fulfilled' && (levelsData.value as any)?.length > 0 ? (levelsData.value as any) : [])
      setClasses(classesData.status === 'fulfilled' ? ((classesData.value as any) || []) : [])

      try {
        const subjectsData = await subjectsApi.getAll()
        const subjectsArray = Array.isArray(subjectsData) ? subjectsData : (subjectsData?.data || [])
        setSubjects(subjectsArray)
      } catch (e: any) {
        console.warn('[Subjects] Failed to load subjects:', e.message)
        setSubjects([])
      }

      try {
        const teachersData: any = await apiService.get('/teachers?status=active')
        const teachersArray: any[] = Array.isArray(teachersData) ? teachersData : (teachersData?.data || teachersData?.items || [])
        const formattedTeachers = teachersArray.map((t: any) => ({
          id: t.id,
          first_name: t.first_name || t.user?.first_name || '',
          last_name: t.last_name || t.user?.last_name || '',
          full_name: t.full_name || `${t.last_name || t.user?.last_name || ''} ${t.first_name || t.user?.first_name || ''}`.trim()
        }))
        setTeachers(formattedTeachers)
      } catch (e: any) {
        console.warn('[Teachers] Failed to load teachers:', e.message)
        setTeachers([])
      }

      try {
        const scData = await subjectSpecialtiesApi.getAll ? await subjectSpecialtiesApi.getAll() : []
        setSubjectSpecialties(Array.isArray(scData) ? scData : ((scData as any)?.data || []))
      } catch (e: any) {
        console.warn('[SubjectSpecialties] Failed to load:', e.message)
        setSubjectSpecialties([])
      }
    } catch (e: any) { 
      console.error('Load error:', e)
      toast.error(txt.loadingError)
    } finally { setLoading(false) }
  }, [txt.loadingError, user?.school_id])

  const loadSpecialtyGroups = useCallback(async () => {
    try { const d = await specialtiesApi.getSpecialtyGroups(); setSpecialtyGroups(d || []); return d || [] }
    catch { setSpecialtyGroups([]); return [] }
  }, [])

  const loadSpecialtiesForGroups = useCallback(async (groupIds: number[]) => {
    if (groupIds.length === 0) { setSpecialties([]); return }
    const all: Specialty[] = []
    const results = await Promise.allSettled(groupIds.map(id => specialtiesApi.getSpecialties(id)))
    results.forEach((r: any) => { if (r.status === 'fulfilled' && Array.isArray(r.value)) all.push(...r.value) })
    setSpecialties(all)
  }, [])

  const loadSpecialtiesByCycle = useCallback(async (cycleId: number) => {
    const groups = specialtyGroups.length > 0 ? specialtyGroups : await loadSpecialtyGroups()
    const groupsForCycle = groups.filter((g: SpecialtyGroup) => g.cycle_id === cycleId)
    if (groupsForCycle.length > 0) {
      await loadSpecialtiesForGroups(groupsForCycle.map((g: SpecialtyGroup) => g.id))
    } else {
      const cycle = cycles.find(c => c.id === cycleId)
      if (cycle) {
        const groupsForSection = groups.filter((g: SpecialtyGroup) => g.section_id === cycle.section_id)
        if (groupsForSection.length > 0) await loadSpecialtiesForGroups(groupsForSection.map((g: SpecialtyGroup) => g.id))
        else setSpecialties([])
      } else setSpecialties([])
    }
  }, [cycles, loadSpecialtyGroups, loadSpecialtiesForGroups, specialtyGroups])

  // Year sync
  useEffect(() => {
    const storedYearId = localStorage.getItem('lybibal-selected-year-id')
    if (storedYearId) setSelectedAcademicYear(Number(storedYearId))
    const handleYearChange = (e: any) => {
      const yearId = e.detail?.year_id || e.detail?.id
      if (yearId) setSelectedAcademicYear(Number(yearId))
    }
    window.addEventListener('lybibal-year-change', handleYearChange)
    return () => window.removeEventListener('lybibal-year-change', handleYearChange)
  }, [])

  const currentYearId = useMemo(() => {
    const userSchoolId = user?.school_id
    if (!userSchoolId) return null
    return academicYears.find(y => y.school_id === userSchoolId && y.is_current)?.id ?? null
  }, [academicYears, user?.school_id])

  const canPerformCrud = useMemo(() => {
    if (currentYearId === null) return false
    return selectedAcademicYear === currentYearId
  }, [selectedAcademicYear, currentYearId])

  useEffect(() => { loadAllData(); loadSpecialtyGroups() }, [loadAllData, loadSpecialtyGroups])

  useEffect(() => {
    if (selectedCycle) loadSpecialtiesByCycle(Number(selectedCycle))
    else {
      const loadAll = async () => {
        const groups = specialtyGroups.length > 0 ? specialtyGroups : await loadSpecialtyGroups()
        if (groups.length > 0) await loadSpecialtiesForGroups(groups.map((g: SpecialtyGroup) => g.id))
      }
      loadAll()
    }
  }, [selectedCycle, loadSpecialtiesByCycle, loadSpecialtyGroups, loadSpecialtiesForGroups, specialtyGroups])

  const filteredCycles = selectedSection ? cycles.filter(c => c.section_id === Number(selectedSection)) : cycles

  const filteredSpecialties = useMemo(() => {
    if (!selectedCycle) return specialties
    const cycleId = Number(selectedCycle)
    const groupsForCycle = specialtyGroups.filter(g => g.cycle_id === cycleId)
    if (groupsForCycle.length > 0) {
      const groupIds = groupsForCycle.map(g => g.id)
      return specialties.filter(s => groupIds.includes(s.specialty_group_id))
    }
    const cycle = cycles.find(c => c.id === cycleId)
    if (cycle) {
      const groupsForSection = specialtyGroups.filter(g => g.section_id === cycle.section_id)
      const groupIds = groupsForSection.map(g => g.id)
      return specialties.filter(s => groupIds.includes(s.specialty_group_id))
    }
    return specialties
  }, [specialties, specialtyGroups, selectedCycle, cycles])

  const filteredLevels = selectedCycle ? levels.filter(l => l.cycle_id === Number(selectedCycle))
    : selectedSection ? levels.filter(l => { const c = cycles.find(cc => cc.id === l.cycle_id); return c && c.section_id === Number(selectedSection) }) : levels

  const getClassDisplayData = useCallback((cls: ClassItem) => {
    const level = levels.find(l => l.id === cls.level_id)
    const cycle = level ? cycles.find(c => c.id === level.cycle_id) : undefined
    const section = cycle ? sections.find(s => s.id === cycle.section_id) : undefined
    const specialty = specialties.find(s => s.id === cls.specialty_id)
    return {
      levelName: level?.label_fr || cls.level_name || '—',
      cycleName: cycle?.label_fr || '—',
      sectionName: section?.label_fr || '—',
      sectionId: section?.id,
      cycleId: cycle?.id,
      specialtyName: specialty?.label_fr || cls.specialty_name || '—',
      specialtyCode: specialty?.code || ''
    }
  }, [levels, cycles, sections, specialties])

  // ============================================================================
  // CLASS CRUD
  // ============================================================================

  const handleCreateClass = async () => {
    if (!canPerformCrud) { toast.error(txt.notCurrentYear); return }
    if (!selectedSpecialty || !selectedLevel || !className.trim() || !classAbbrev.trim()) {
      toast.error(txt.fillAllFields)
      return
    }
    try {
      const payload: any = {
        level_id: Number(selectedLevel),
        specialty_id: Number(selectedSpecialty),
        code: classAbbrev.trim().toUpperCase(),
        abbreviation: classAbbrev.trim().toUpperCase(),
        name: className.trim(),
        capacity: 50,
        status: 'open'
      }
      if (!isPermanentClass) {
        const yearToUse = (selectedAcademicYear !== '' && selectedAcademicYear !== null) ? selectedAcademicYear : currentYearId
        if (yearToUse) { payload.academic_year_id = Number(yearToUse) }
        else { toast.error(lang === 'fr' ? 'Aucune annee active definie' : 'No active year defined'); return }
      } else {
        payload.academic_year_id = null
      }
      await classesApi.create(payload)
      toast.success(txt.classCreated)
      setClassName(''); setClassAbbrev('')
      await loadAllData()
    } catch (error: any) {
      console.error('[Class] Create error:', error)
      toast.error(error?.response?.data?.detail || error?.message || txt.createError)
    }
  }

  const handleToggleStatus = async (cls: ClassItem) => {
    if (!canPerformCrud) { toast.error(txt.notCurrentYear); return }
    try {
      const ns = cls.status === 'open' ? 'closed' : 'open'
      await classesApi.update(cls.id, { status: ns })
      setClasses(prev => prev.map(c => c.id === cls.id ? { ...c, status: ns } : c))
      toast.success(txt.statusUpdated)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || txt.updateError)
    }
  }

  const startEdit = (cls: ClassItem) => {
    setEditingId(cls.id)
    setEditName(cls.name)
    setEditAbbrev(cls.abbreviation)
    setEditSpecialty(cls.specialty_id || '')
    setEditLevel(cls.level_id || '')
  }
  const cancelEdit = () => {
    setEditingId(null)
    setEditSpecialty('')
    setEditLevel('')
  }
  const saveEdit = async (id: number) => {
    if (!canPerformCrud) { toast.error(txt.notCurrentYear); return }
    try {
      await classesApi.update(id, {
        name: editName.trim(),
        abbreviation: editAbbrev.trim().toUpperCase(),
        specialty_id: editSpecialty !== '' ? Number(editSpecialty) : undefined,
        level_id: editLevel !== '' ? Number(editLevel) : undefined,
      })
      setClasses(prev => prev.map(c => c.id === id ? {
        ...c,
        name: editName.trim(),
        abbreviation: editAbbrev.trim().toUpperCase(),
        specialty_id: editSpecialty !== '' ? Number(editSpecialty) : c.specialty_id,
        level_id: editLevel !== '' ? Number(editLevel) : c.level_id,
      } : c))
      setEditingId(null)
      setEditSpecialty('')
      setEditLevel('')
      toast.success(txt.classUpdated)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || txt.updateError)
    }
  }

  // ============================================================================
  // CYCLE / LEVEL CRUD
  // ============================================================================

  const handleCreateCycle = async () => {
    if (!selectedSection || !cycleForm.code.trim() || !cycleForm.label_fr.trim()) { toast.error(txt.fillAllFields); return }
    try {
      await cyclesApi.create({ section_id: Number(selectedSection), code: cycleForm.code.trim().toUpperCase(), label_fr: cycleForm.label_fr.trim(), label_en: cycleForm.label_en.trim() || cycleForm.label_fr.trim(), order: cycleForm.order })
      toast.success(txt.cycleCreated); setCycleForm({ code: '', label_fr: '', label_en: '', order: 1 }); await loadAllData()
    } catch (error: any) { toast.error(error?.response?.data?.detail || error?.message || txt.createError) }
  }

  const handleUpdateCycle = async (id: number) => {
    try {
      await cyclesApi.update(id, { code: cycleForm.code.trim().toUpperCase(), label_fr: cycleForm.label_fr.trim(), label_en: cycleForm.label_en.trim(), order: cycleForm.order })
      toast.success(txt.cycleUpdated); setEditingCycleId(null); setCycleForm({ code: '', label_fr: '', label_en: '', order: 1 }); await loadAllData()
    } catch (error: any) { toast.error(error?.response?.data?.detail || txt.updateError) }
  }

  const handleCreateLevel = async () => {
    if (!selectedCycle || !levelForm.code.trim() || !levelForm.label_fr.trim()) { toast.error(txt.fillAllFields); return }
    try {
      await levelsApi.create({ cycle_id: Number(selectedCycle), code: levelForm.code.trim().toLowerCase(), label_fr: levelForm.label_fr.trim(), label_en: levelForm.label_en.trim() || levelForm.label_fr.trim(), order: levelForm.order })
      toast.success(txt.levelCreated); setLevelForm({ code: '', label_fr: '', label_en: '', order: 1 }); await loadAllData()
    } catch (error: any) { toast.error(error?.response?.data?.detail || error?.message || txt.createError) }
  }

  const handleUpdateLevel = async (id: number) => {
    try {
      await levelsApi.update(id, { code: levelForm.code.trim().toLowerCase(), label_fr: levelForm.label_fr.trim(), label_en: levelForm.label_en.trim(), order: levelForm.order })
      toast.success(txt.levelUpdated); setEditingLevelId(null); setLevelForm({ code: '', label_fr: '', label_en: '', order: 1 }); await loadAllData()
    } catch (error: any) { toast.error(error?.response?.data?.detail || txt.updateError) }
  }

  // ============================================================================
  // SUBJECT CRUD
  // ============================================================================

  const openSubjectModal = (mode: 'create' | 'edit', subject?: Subject) => {
    if (!canManageSubjects) { toast.error(txt.permissionDenied); return }
    setSubjectModalMode(mode)
    if (mode === 'edit' && subject) {
      setEditingSubjectId(subject.id)
      setSubjectForm({
        code: subject.code,
        label_fr: subject.label_fr,
        label_en: subject.label_en || '',
        abbreviation: subject.abbreviation || '',
        group_name: subject.group_name || '',
        section_id: subject.section_id ? String(subject.section_id) : '',
        specialty_id: subject.specialty_id ? String(subject.specialty_id) : ''
      })
    } else {
      setEditingSubjectId(null)
      setSubjectForm({ code: '', label_fr: '', label_en: '', abbreviation: '', group_name: '', section_id: '', specialty_id: '' })
    }
    setSubjectModalOpen(true)
  }

  const closeSubjectModal = () => {
    setSubjectModalOpen(false)
    setEditingSubjectId(null)
    setSubjectForm({ code: '', label_fr: '', label_en: '', abbreviation: '', group_name: '', section_id: '', specialty_id: '' })
  }

  const saveSubjectGroups = (groups: string[]) => {
    setSubjectGroups(groups)
    try { localStorage.setItem('lybibal-subject-groups', JSON.stringify(groups)) } catch (e) { /* ignore */ }
  }

  const handleAddGroup = () => {
    const name = newGroupName.trim().toUpperCase()
    if (!name) { toast.error(lang === 'fr' ? 'Veuillez saisir un nom' : 'Please enter a name'); return }
    if (subjectGroups.includes(name)) { toast.error(txt.groupExists); return }
    const updated = [...subjectGroups, name]
    saveSubjectGroups(updated)
    setNewGroupName('')
    toast.success(txt.groupAdded)
  }

  const handleDeleteGroup = (group: string) => {
    const updated = subjectGroups.filter(g => g !== group)
    saveSubjectGroups(updated)
    if (subjectFilterGroup === group) setSubjectFilterGroup('')
    if (subjectForm.group_name === group) setSubjectForm(prev => ({ ...prev, group_name: '' }))
    toast.success(txt.groupDeleted)
  }

  const openGroupModal = () => { setNewGroupName(''); setGroupModalOpen(true) }
  const closeGroupModal = () => { setGroupModalOpen(false); setNewGroupName('') }

  const handleSaveSubject = async () => {
    if (!canManageSubjects) { toast.error(txt.permissionDenied); return }
    if (!subjectForm.code.trim() || !subjectForm.label_fr.trim() || !subjectForm.abbreviation.trim()) {
      toast.error(txt.fillSubjectFields)
      return
    }
    setIsSubmitting(true)
    try {
      const payload = {
        code: subjectForm.code.trim().toUpperCase(),
        label_fr: subjectForm.label_fr.trim(),
        label_en: subjectForm.label_en.trim() || subjectForm.label_fr.trim(),
        abbreviation: subjectForm.abbreviation.trim().toUpperCase(),
        group_name: subjectForm.group_name.trim().toUpperCase() || null,
        section_id: subjectForm.section_id ? Number(subjectForm.section_id) : null,
        specialty_id: subjectForm.specialty_id ? Number(subjectForm.specialty_id) : null,
        status: 'ACTIVE',
        school_id: user?.school_id || 1,
        academic_year_id: selectedAcademicYear ? Number(selectedAcademicYear) : null
      }
      if (subjectModalMode === 'create') {
        await subjectsApi.create(payload)
        toast.success(txt.subjectCreated)
      } else if (editingSubjectId) {
        await subjectsApi.update(editingSubjectId, payload)
        toast.success(txt.subjectUpdated)
      }
      closeSubjectModal()
      await loadAllData()
    } catch (error: any) {
      console.error('[Subjects] Save error:', error)
      toast.error(error?.response?.data?.detail || error?.message || txt.createError)
    } finally { setIsSubmitting(false) }
  }

  const handleToggleSubjectStatus = async (subject: Subject) => {
    if (!canManageSubjects) { toast.error(txt.permissionDenied); return }
    try {
      const ns = subject.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      try {
        await apiService.patch(`/subjects/${subject.id}/toggle-status`, {})
      } catch (e: any) {
        if (e.response?.status === 404) {
          await subjectsApi.update(subject.id, { status: ns })
        } else { throw e }
      }
      setSubjects(prev => prev.map(s => s.id === subject.id ? { ...s, status: ns } : s))
      toast.success(txt.statusUpdated)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || txt.updateError)
    }
  }

  // ============================================================================
  // ASSIGN AP
  // ============================================================================

  const openApModal = (subject: Subject) => {
    if (!canManageSubjects) { toast.error(txt.permissionDenied); return }
    setApSubjectId(subject.id)
    setApTeacherId(subject.teacher_id || '')
    setApModalOpen(true)
  }

  const closeApModal = () => { setApModalOpen(false); setApSubjectId(null); setApTeacherId('') }

  const handleAssignAP = async () => {
    if (!apSubjectId) return
    setIsSubmitting(true)
    try {
      const teacherIdValue = apTeacherId ? Number(apTeacherId) : null
      await apiService.patch(`/teachers/subjects/${apSubjectId}/assign-ap`, { teacher_id: teacherIdValue })
      toast.success(lang === 'fr' ? 'AP assigné avec succès' : 'AP assigned successfully')
      closeApModal()
      await loadAllData()
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || (lang === 'fr' ? "Erreur lors de l'assignation" : 'Assignment error'))
    } finally { setIsSubmitting(false) }
  }

  // ============================================================================
  // SUBJECT-CLASS CRUD
  // ============================================================================

  const openScModal = (mode: 'create' | 'edit' | 'bulk-cycle', sc?: SubjectSpecialty) => {
    if (!canManageSubjects) { toast.error(txt.permissionDenied); return }
    setScModalMode(mode)
    if (mode === 'edit' && sc) {
      setEditingScId(sc.id)
      setScForm({
        subject_id: String(sc.subject_id),
        specialty_id: String(sc.specialty_id),
        cycle_id: String(sc.cycle_id || ''),
        level_id: sc.level_id ? String(sc.level_id) : '',
        coefficient: String(sc.coefficient || 1),
        hours_per_week: String(sc.hours_per_week || 2),
        status: sc.status || 'ACTIVE',
        subsystem: ''
      })
    } else if (mode === 'bulk-cycle') {
      setEditingScId(null)
      setScForm({ subject_id: '', specialty_id: '', cycle_id: '', level_id: '', coefficient: '1', hours_per_week: '2', status: 'ACTIVE', subsystem: '' })
    } else {
      setEditingScId(null)
      setScForm({ subject_id: '', specialty_id: '', cycle_id: '', level_id: '', coefficient: '1', hours_per_week: '2', status: 'ACTIVE', subsystem: '' })
    }
    setScModalOpen(true)
  }

  const closeScModal = () => {
    setScModalOpen(false)
    setEditingScId(null)
    setScForm({ subject_id: '', specialty_id: '', cycle_id: '', level_id: '', coefficient: '1', hours_per_week: '2', status: 'ACTIVE', subsystem: '' })
  }

  const handleSaveSubjectSpecialty = async () => {
    if (!canManageSubjects) { toast.error(txt.permissionDenied); return }
    if (scModalMode !== 'bulk-cycle' && (!scForm.subject_id || !scForm.specialty_id)) {
      toast.error(txt.fillAllFields)
      return
    }
    if (scModalMode === 'bulk-cycle' && (!scForm.subject_id || !scForm.cycle_id || !scForm.specialty_id)) {
      toast.error(txt.fillAllFields)
      return
    }
    setIsSubmitting(true)
    try {
      if (scModalMode === 'bulk-cycle') {
        const result = await apiService.post('/subject-specialties/bulk-by-cycle', {
          subject_id: Number(scForm.subject_id),
          specialty_id: Number(scForm.specialty_id),
          cycle_id: Number(scForm.cycle_id),
          coefficient: parseFloat(scForm.coefficient) || 1,
          hours_per_week: parseFloat(scForm.hours_per_week) || 2,
          status: scForm.status,
          school_id: user?.school_id || 1,
          academic_year_id: selectedAcademicYear ? Number(selectedAcademicYear) : undefined,
          overwrite_existing: false
        })
        toast.success((result as any)?.message || txt.bulkAssignmentSuccess)
      } else {
        const payload = {
          subject_id: Number(scForm.subject_id),
          specialty_id: Number(scForm.specialty_id),
          level_id: scForm.level_id && Number(scForm.level_id) > 0 ? Number(scForm.level_id) : undefined,
          coefficient: parseFloat(scForm.coefficient) || 1,
          hours_per_week: parseFloat(scForm.hours_per_week) || 2,
          status: scForm.status,
          school_id: user?.school_id || 1,
          academic_year_id: selectedAcademicYear ? Number(selectedAcademicYear) : undefined
        }
        if (scModalMode === 'create') {
          await subjectSpecialtiesApi.create(payload)
          toast.success(txt.subjectSpecialtyCreated)
        } else if (editingScId && subjectSpecialtiesApi.update) {
          await subjectSpecialtiesApi.update(editingScId, payload)
          toast.success(txt.subjectSpecialtyUpdated)
        }
      }
      closeScModal()
      await loadAllData()
    } catch (error: any) {
      console.error('[SubjectSpecialty] Save error:', error)
      toast.error(error?.response?.data?.detail || error?.message || txt.createError)
    } finally { setIsSubmitting(false) }
  }

  // ============================================================================
  // COMPETENCY CRUD
  // ============================================================================

  const loadCompetencies = useCallback(async (params?: any) => {
    try {
      const res = await apiService.get('/competencies', { params: { ...params, school_id: userSchoolId || undefined } })
      const data = Array.isArray(res) ? res : ((res as any)?.data || [])
      setCompetencies(data)
    } catch (e: any) {
      console.warn('[Competencies] load error:', e.message)
      setCompetencies([])
    }
  }, [userSchoolId])

  // Charger toutes les compétences quand on ouvre l'onglet Compétences
  useEffect(() => {
    if (activeTab === 'competencies') {
      loadCompetencies()
    }
  }, [activeTab, loadCompetencies])

  const openCompetencyModal = (sc: SubjectSpecialty) => {
    if (!canManageSubjects) { toast.error(txt.permissionDenied); return }
    setCompetencyModalSubjectSpecialty(sc)
    setCompetencyForm({ trimester: 1, number: 1, label_fr: '', label_en: '', abbreviation: '', description: '' })
    setEditingCompetencyId(null)
    setModalCopySource('')
    loadCompetencies({ subject_specialty_id: sc.id })
    setCompetencyModalOpen(true)
  }

  const closeCompetencyModal = () => {
    setCompetencyModalOpen(false)
    setCompetencyModalSubjectSpecialty(null)
    setEditingCompetencyId(null)
    setModalCopySource('')
    setCompetencyForm({ trimester: 1, number: 1, label_fr: '', label_en: '', abbreviation: '', description: '' })
    // Recharger TOUTES les competences : le modal avait filtre l'etat global sur une seule matiere
    loadCompetencies()
  }

  const handleSaveCompetency = async () => {
    if (!competencyModalSubjectSpecialty) return
    if (!competencyForm.label_fr.trim()) { toast.error(txt.fillAllFields); return }
    setIsSubmitting(true)
    try {
      const payload = {
        subject_specialty_id: competencyModalSubjectSpecialty.id,
        trimester: competencyForm.trimester,
        number: competencyForm.number,
        label_fr: competencyForm.label_fr.trim(),
        label_en: competencyForm.label_en.trim() || competencyForm.label_fr.trim(),
        abbreviation: competencyForm.abbreviation.trim().toUpperCase() || null,
        description: competencyForm.description.trim() || null,
        status: 'ACTIVE',
        school_id: user?.school_id || 1,
        academic_year_id: selectedAcademicYear ? Number(selectedAcademicYear) : null
      }
      if (editingCompetencyId) {
        await apiService.put(`/competencies/${editingCompetencyId}`, payload)
        toast.success(txt.competencyUpdated)
      } else {
        await apiService.post('/competencies', payload)
        toast.success(txt.competencyCreated)
      }
      setCompetencyForm({ trimester: 1, number: 1, label_fr: '', label_en: '', abbreviation: '', description: '' })
      setEditingCompetencyId(null)
      await loadCompetencies({ subject_specialty_id: competencyModalSubjectSpecialty.id })
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || error?.message || txt.createError)
    } finally { setIsSubmitting(false) }
  }

  const startEditCompetency = (comp: SubjectCompetency) => {
    setEditingCompetencyId(comp.id)
    setCompetencyForm({
      trimester: comp.trimester,
      number: comp.number,
      label_fr: comp.label_fr,
      label_en: comp.label_en || '',
      abbreviation: comp.abbreviation || '',
      description: comp.description || ''
    })
  }

  const handleDeleteCompetency = async (compId: number) => {
    if (!canManageSubjects) { toast.error(txt.permissionDenied); return }
    try {
      await apiService.delete(`/competencies/${compId}`)
      toast.success(txt.competencyDeleted)
      if (competencyModalSubjectSpecialty) {
        await loadCompetencies({ subject_specialty_id: competencyModalSubjectSpecialty.id })
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || txt.deleteError)
    }
  }

  /** ✅ Supprime TOUTES les compétences de la matière ouverte dans le modal. */
  const handleDeleteAllSubjectCompetencies = async () => {
    if (!competencyModalSubjectSpecialty) return
    const comps = competencies.filter(cc => cc.subject_specialty_id === competencyModalSubjectSpecialty!.id)
    if (comps.length === 0) { toast.info(txt.noCompetencies); setConfirmDeleteAllSubject(false); return }
    setDeletingAllSubject(true)
    let ok = 0, failed = 0
    try {
      for (const cc of comps) {
        try { await apiService.delete(`/competencies/${cc.id}`); ok++ }
        catch (e) { failed++; console.warn('[Competencies] delete failed', cc.id, e) }
      }
      toast.success(`${ok} ${lang === 'fr' ? 'compétence(s) supprimée(s)' : 'competency(ies) deleted'}${failed > 0 ? ` — ${failed} ${lang === 'fr' ? 'échec(s)' : 'failed'}` : ''}`)
      setConfirmDeleteAllSubject(false)
      await loadCompetencies({ subject_specialty_id: competencyModalSubjectSpecialty.id })
      await loadCompetencies()
    } finally { setDeletingAllSubject(false) }
  }

  /** ✅ Supprime TOUTES les compétences visibles dans les filtres actuels (niveau/spécialité/matière). */
  const handleDeleteAllCompetencies = async () => {
    const scIds = competencyFilteredSubjectSpecialtiesAll.map(sc => sc.id)
    const comps = competencies.filter(cc => scIds.includes(cc.subject_specialty_id))
    if (comps.length === 0) { toast.info(txt.noCompetencies); setConfirmDeleteAllGlobal(false); return }
    setDeletingAllGlobal(true)
    let ok = 0, failed = 0
    try {
      for (const cc of comps) {
        try { await apiService.delete(`/competencies/${cc.id}`); ok++ }
        catch (e) { failed++; console.warn('[Competencies] delete failed', cc.id, e) }
      }
      toast.success(`${ok} ${lang === 'fr' ? 'compétence(s) supprimée(s)' : 'competency(ies) deleted'}${failed > 0 ? ` — ${failed} ${lang === 'fr' ? 'échec(s)' : 'failed'}` : ''}`)
      setConfirmDeleteAllGlobal(false)
      await loadCompetencies()
    } finally { setDeletingAllGlobal(false) }
  }

  // ============================================================================
  // COPIE DE COMPETENCES ENTRE SPECIALITES (niveau + specialite)
  // ============================================================================

  /** Retourne les lignes subject_specialties d'une specialite ( niveau optionnel ). */
  const getRowsForSpecialty = useCallback((specialtyId: number, levelId?: number | '') => {
    return subjectSpecialties.filter(sc =>
      sc.specialty_id === specialtyId &&
      (!levelId || sc.level_id === Number(levelId))
    )
  }, [subjectSpecialties])

  /** Copie les competences d'une ligne source vers une ligne cible (meme matiere). */
  const copyCompetenciesBetweenRows = useCallback(async (
    sourceScId: number,
    targetScId: number,
    overwrite: boolean
  ): Promise<{ created: number; skipped: number }> => {
    const sourceComps = competencies.filter(c => c.subject_specialty_id === sourceScId)
    if (sourceComps.length === 0) return { created: 0, skipped: 0 }
    const existing = competencies.filter(c => c.subject_specialty_id === targetScId)
    let created = 0
    let skipped = 0
    if (overwrite && existing.length > 0) {
      for (const ex of existing) {
        await apiService.delete(`/competencies/${ex.id}`)
      }
      setCompetencies(prev => prev.filter(c => c.subject_specialty_id !== targetScId))
    }
    const existingKeys = new Set(
      (overwrite ? [] : existing).map(c => `${c.trimester}-${c.number}`)
    )
    const items = sourceComps
      .filter(c => !existingKeys.has(`${c.trimester}-${c.number}`))
      .map(c => ({
        subject_specialty_id: targetScId,
        trimester: c.trimester,
        number: c.number,
        label_fr: c.label_fr,
        label_en: c.label_en || c.label_fr,
        abbreviation: c.abbreviation || null,
        description: c.description || null,
        status: 'ACTIVE',
        school_id: user?.school_id || 1,
        academic_year_id: selectedAcademicYear ? Number(selectedAcademicYear) : null
      }))
    skipped += sourceComps.filter(c => existingKeys.has(`${c.trimester}-${c.number}`)).length
    if (items.length > 0) {
      try {
        const res: any = await apiService.post('/competencies/bulk', { items })
        if (typeof res?.created === 'number') created = res.created
        else created = items.length
      } catch {
        for (const it of items) { await apiService.post('/competencies', it) }
        created = items.length
      }
    }
    return { created, skipped }
  }, [competencies, user?.school_id, selectedAcademicYear])

  /** Copie globale : toutes les matieres d'une specialite source vers une cible. */
  const handleGlobalCopy = async () => {
    if (!compCopySource || !compCopyTarget) {
      toast.error(txt.fillAllFields)
      return
    }
    if (compCopySource === compCopyTarget) {
      toast.error(lang === 'fr' ? 'Les specialites doivent etre differentes' : 'Specialties must be different')
      return
    }
    setIsCopying(true)
    try {
      const sourceRows = getRowsForSpecialty(Number(compCopySource), compCopyLevel)
      const targetRows = getRowsForSpecialty(Number(compCopyTarget), compCopyLevel)
      let created = 0
      let skipped = 0
      let matched = 0
      for (const tgt of targetRows) {
        // Source : meme matiere + meme niveau (sinon meme matiere quel que soit le niveau)
        const src = sourceRows.find(s => s.subject_id === tgt.subject_id && s.level_id === tgt.level_id)
          || sourceRows.find(s => s.subject_id === tgt.subject_id)
        if (!src) continue
        matched++
        const r = await copyCompetenciesBetweenRows(src.id, tgt.id, compCopyOverwrite)
        created += r.created
        skipped += r.skipped
      }
      if (matched === 0 || created === 0) {
        toast.info(txt.copyNoMatch)
      } else {
        toast.success(`${created} ${txt.copySuccess}${skipped > 0 ? ` — ${skipped} ${txt.copySkipped}` : ''}`)
      }
      setCompCopyModalOpen(false)
      setCompCopySource(''); setCompCopyTarget(''); setCompCopyLevel(''); setCompCopyOverwrite(false)
      await loadCompetencies()
    } catch (e: any) {
      console.error('[Competencies] Copy error:', e)
      toast.error(e?.response?.data?.detail || txt.copyError)
    } finally { setIsCopying(false) }
  }

  /** Copie depuis le modal (une matiere, une autre specialite). */
  const handleModalCopy = async () => {
    if (!competencyModalSubjectSpecialty || !modalCopySource) return
    setIsCopying(true)
    try {
      const r = await copyCompetenciesBetweenRows(Number(modalCopySource), competencyModalSubjectSpecialty.id, false)
      if (r.created === 0) toast.info(txt.copyNoMatch)
      else toast.success(`${r.created} ${txt.copySuccess}${r.skipped > 0 ? ` — ${r.skipped} ${txt.copySkipped}` : ''}`)
      setModalCopySource('')
      await loadCompetencies({ subject_specialty_id: competencyModalSubjectSpecialty.id })
      await loadCompetencies()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || txt.copyError)
    } finally { setIsCopying(false) }
  }

  // ============================================================================
  // DUPLICATE
  // ============================================================================

  const handleDuplicate = async () => {
    if (!duplicateSourceClass || !duplicateTargetClass) {
      toast.error(lang === 'fr' ? 'Veuillez selectionner les deux specialites' : 'Please select both specialties')
      return
    }
    if (duplicateSourceClass === duplicateTargetClass) {
      toast.error(lang === 'fr' ? 'Les specialites doivent etre differentes' : 'Specialties must be different')
      return
    }
    setIsSubmitting(true)
    try {
      const result = await subjectSpecialtiesApi.duplicate({
        source_specialty_id: Number(duplicateSourceClass),
        target_specialty_id: Number(duplicateTargetClass),
        school_id: user?.school_id || 1,
        academic_year_id: selectedAcademicYear ? Number(selectedAcademicYear) : undefined,
        overwrite_existing: duplicateOverwrite
      })
      toast.success((result as any).message || (lang === 'fr' ? 'Duplication reussie' : 'Duplication successful'))
      setDuplicateModalOpen(false)
      setDuplicateSourceClass(''); setDuplicateTargetClass(''); setDuplicateOverwrite(false)
      await loadAllData()
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || error?.message || (lang === 'fr' ? 'Erreur lors de la duplication' : 'Duplication error'))
    } finally { setIsSubmitting(false) }
  }

  // ============================================================================
  // DELETE MODAL
  // ============================================================================

  const openDeleteModal = (id: number, type: 'subject' | 'cycle' | 'level' | 'class' | 'subject-class' | 'competency', name: string) => {
    if (!canManageSubjects && type !== 'class') { toast.error(txt.permissionDenied); return }
    setDeleteTarget({ id, type, name })
    setDeleteModalOpen(true)
  }

  const closeDeleteModal = () => { setDeleteModalOpen(false); setDeleteTarget(null) }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    if (!canPerformCrud && deleteTarget.type === 'class') { toast.error(txt.notCurrentYear); return }
    if (!canManageSubjects && deleteTarget.type !== 'class') { toast.error(txt.permissionDenied); return }
    setIsSubmitting(true)
    try {
      switch (deleteTarget.type) {
        case 'subject': await subjectsApi.delete(deleteTarget.id); toast.success(txt.subjectDeleted); break
        case 'subject-class': if (subjectSpecialtiesApi.delete) await subjectSpecialtiesApi.delete(deleteTarget.id); toast.success(txt.subjectSpecialtyDeleted); break
        case 'competency': await apiService.delete(`/competencies/${deleteTarget.id}`); toast.success(txt.competencyDeleted); break
        case 'cycle': await cyclesApi.delete(deleteTarget.id); toast.success(txt.cycleDeleted); break
        case 'level': await levelsApi.delete(deleteTarget.id); toast.success(txt.levelDeleted); break
        case 'class': await classesApi.delete(deleteTarget.id); toast.success(txt.classDeleted); break
      }
      closeDeleteModal()
      await loadAllData()
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || txt.deleteError)
    } finally { setIsSubmitting(false) }
  }

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredClasses = useMemo(() => classes.filter(c => {
    const d = getClassDisplayData(c)
    const matchesSearch = !searchTerm || c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.abbreviation.toLowerCase().includes(searchTerm.toLowerCase()) || d.specialtyName.toLowerCase().includes(searchTerm.toLowerCase()) || d.levelName.toLowerCase().includes(searchTerm.toLowerCase()) || d.cycleName.toLowerCase().includes(searchTerm.toLowerCase()) || d.sectionName.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesSpecialty = !selectedSpecialty || c.specialty_id === Number(selectedSpecialty)
    const matchesLevel = !selectedLevel || c.level_id === Number(selectedLevel)
    const matchesCycle = !selectedCycle || (d.cycleId === Number(selectedCycle))
    const matchesSection = !selectedSection || (d.sectionId === Number(selectedSection))
    const matchesSchool = !userSchoolId || getClassSchoolId(c, levels, cycles, sections) === userSchoolId
    const matchesYear = selectedAcademicYear === '' || selectedAcademicYear === null || selectedAcademicYear === undefined
      || c.academic_year_id === Number(selectedAcademicYear)
      || c.academic_year_id === null
      || c.academic_year_id === undefined
    return matchesSearch && matchesSpecialty && matchesLevel && matchesCycle && matchesSection && matchesSchool && matchesYear
  }), [classes, getClassDisplayData, searchTerm, selectedSpecialty, selectedLevel, selectedCycle, selectedSection, userSchoolId, sections, levels, cycles, selectedAcademicYear])

  const filteredSubjects = useMemo(() => {
    let result = subjects.filter(s => !userSchoolId || s.school_id === userSchoolId || s.school_id === null || s.school_id === undefined)
    if (subjectSearchTerm) {
      const q = subjectSearchTerm.toLowerCase()
      result = result.filter(s =>
        s.code.toLowerCase().includes(q) ||
        s.label_fr.toLowerCase().includes(q) ||
        s.label_en?.toLowerCase().includes(q) ||
        s.abbreviation?.toLowerCase().includes(q) ||
        (s.section_name && s.section_name.toLowerCase().includes(q)) ||
        (s.specialty_name && s.specialty_name.toLowerCase().includes(q))
      )
    }
    if (subjectFilterStatus) result = result.filter(s => s.status === subjectFilterStatus)
    if (subjectFilterSection) result = result.filter(s => s.section_id === Number(subjectFilterSection))
    if (subjectFilterGroup) result = result.filter(s => s.group_name === subjectFilterGroup)
    result = [...result].sort((a, b) => {
      const dir = subjectSortDir === 'asc' ? 1 : -1
      switch (subjectSortField) {
        case 'code': return (a.code || '').localeCompare(b.code || '') * dir
        case 'label_fr': return (a.label_fr || '').localeCompare(b.label_fr || '') * dir
        case 'label_en': return (a.label_en || '').localeCompare(b.label_en || '') * dir
        case 'abbreviation': return (a.abbreviation || '').localeCompare(b.abbreviation || '') * dir
        case 'group_name': return (a.group_name || '').localeCompare(b.group_name || '') * dir
        case 'status': return (a.status || '').localeCompare(b.status || '') * dir
        case 'section_name': return ((a.section_name || '') as string).localeCompare(b.section_name || '') * dir
        case 'specialty_name': return ((a.specialty_name || '') as string).localeCompare(b.specialty_name || '') * dir
        case 'created_at': return ((new Date(a.created_at || 0)).getTime() - (new Date(b.created_at || 0)).getTime()) * dir
        default: return 0
      }
    })
    return result
  }, [subjects, subjectSearchTerm, subjectFilterStatus, subjectFilterSection, subjectFilterGroup, subjectSortField, subjectSortDir, userSchoolId])

  const filteredSubjectSpecialties = useMemo(() => {
    let result = subjectSpecialties.filter(sc =>
      !userSchoolId ||
      sc.school_id === userSchoolId ||
      sc.school_id === null ||
      sc.school_id === undefined
    )
    // ✅ TOUTES les attributions sont affichées (1er cycle inclus : 4e/3e ALL-ESP, Form 5A/5B, etc.)
    if (scSearchTerm) {
      const q = scSearchTerm.toLowerCase()
      result = result.filter(sc =>
        (sc.subject_code?.toLowerCase().includes(q)) ||
        (sc.subject_label_fr?.toLowerCase().includes(q)) ||
        (sc.specialty_name?.toLowerCase().includes(q)) ||
        (sc.specialty_code?.toLowerCase().includes(q))
      )
    }
    if (scFilterSpecialty) result = result.filter(sc => sc.specialty_id === Number(scFilterSpecialty))
    if (scFilterSubject) result = result.filter(sc => sc.subject_id === Number(scFilterSubject))
    if (scFilterLevel) result = result.filter(sc => sc.level_id === Number(scFilterLevel))
    return result
  }, [subjectSpecialties, scSearchTerm, scFilterSpecialty, scFilterSubject, scFilterLevel, userSchoolId, levels, cycles, specialties, specialtyGroups])

  // ============================================================================
  // COMPETENCY FILTERED DATA — par niveau ET par specialite (1er ET 2nd cycle)
  // ============================================================================
  const competencyFilteredSubjectSpecialtiesAll = useMemo(() => {
    let result = subjectSpecialties.filter(sc =>
      !userSchoolId ||
      sc.school_id === userSchoolId ||
      sc.school_id === null ||
      sc.school_id === undefined
    )
    // PERMANENT : n'afficher que le 1er cycle (6e, 5e, 4e, 3e / Form 1-4)
    result = result.filter(sc => {
      const level = levels.find(l => l.id === sc.level_id)
      let cycle = level ? cycles.find(c => c.id === level.cycle_id) : undefined
      if (!cycle) {
        const spec = specialties.find(s => s.id === sc.specialty_id)
        const group = spec ? specialtyGroups.find(g => g.id === spec.specialty_group_id) : undefined
        if (group) cycle = cycles.find(c => c.id === group.cycle_id)
      }
      if (!cycle && !level) return false
      const lbl = `${cycle?.label_fr || ''} ${cycle?.label_en || ''} ${level?.label_fr || ''} ${level?.label_en || ''}`
      const cycleIsFirst = cycle ? isFirstCycleLabel(cycle.label_fr || cycle.label_en || '') : false
      const levelIsFirst = level ? isFirstCycleLabel(level.label_fr || level.label_en || '') : false
      // 1er cycle + niveaux anglophones Form 1 à Form 5 (y compris Form 5A/Form 5B)
      return cycleIsFirst || levelIsFirst || /\bform\s*[1-5]\s*[ab]?\b/i.test(lbl)
    })
    // Filtre par section (via level->cycle->section ou specialty->group->section)
    if (competencyFilterSection) {
      result = result.filter(sc => {
        const level = levels.find(l => l.id === sc.level_id)
        const cycle = level ? cycles.find(c => c.id === level.cycle_id) : undefined
        let sectionId: number | undefined = cycle?.section_id
        if (!sectionId) {
          const spec = specialties.find(s => s.id === sc.specialty_id)
          const group = spec ? specialtyGroups.find(g => g.id === spec.specialty_group_id) : undefined
          sectionId = group?.section_id
        }
        return sectionId === Number(competencyFilterSection)
      })
    }
    // Filtre par cycle
    if (competencyFilterCycle) {
      const cycleLevelIds = levels.filter(l => l.cycle_id === Number(competencyFilterCycle)).map(l => l.id)
      result = result.filter(sc => sc.level_id && cycleLevelIds.includes(sc.level_id))
    }
    // Filtre par niveau
    if (competencyFilterLevel) result = result.filter(sc => sc.level_id === Number(competencyFilterLevel))
    // Filtre par specialite
    if (competencyFilterSpecialty) result = result.filter(sc => sc.specialty_id === Number(competencyFilterSpecialty))
    // Filtre par matiere
    if (competencyFilterSubject) result = result.filter(sc => sc.subject_id === Number(competencyFilterSubject))
    // Tri : specialite puis matiere pour un affichage groupe
    return [...result].sort((a, b) => {
      const sp = (a.specialty_name || '').localeCompare(b.specialty_name || '')
      if (sp !== 0) return sp
      return (a.subject_label_fr || '').localeCompare(b.subject_label_fr || '')
    })
  }, [subjectSpecialties, competencyFilterCycle, competencyFilterLevel, competencyFilterSpecialty, competencyFilterSubject, competencyFilterSection, userSchoolId, levels, cycles, specialties, specialtyGroups])

    // ============================================================================
  // COMPETENCY FILTERED DATA — par niveau ET par specialite (1er ET 2nd cycle)

  // ⚠️ PAGINATION — doit être déclarée AVANT competencyGroups
  const competencyTotalPages = Math.ceil(competencyFilteredSubjectSpecialtiesAll.length / competencyPageSize) || 1
  const competencyFilteredSubjectSpecialties = useMemo(() => {
    const start = (competencyCurrentPage - 1) * competencyPageSize
    return competencyFilteredSubjectSpecialtiesAll.slice(start, start + competencyPageSize)
  }, [competencyFilteredSubjectSpecialtiesAll, competencyCurrentPage, competencyPageSize])

  // Groupes par specialite pour l'affichage
  const competencyGroups = useMemo(() => {
    const groups: { specialtyId: number | string; specialtyName: string; specialtyCode: string; subsystem: 'francophone' | 'anglophone' | null; rows: SubjectSpecialty[] }[] = []
    const map = new Map<string, typeof groups[0]>()
    for (const sc of competencyFilteredSubjectSpecialties) {
      const key = String(sc.specialty_id ?? 'none')
      if (!map.has(key)) {
        const spec = specialties.find(s => s.id === sc.specialty_id)
        const level = levels.find(l => l.id === sc.level_id)
        const cycle = level ? cycles.find(c => c.id === level.cycle_id) : undefined
        let sectionId: number | undefined = cycle?.section_id
        if (!sectionId && spec) {
          const grp = specialtyGroups.find(g => g.id === spec.specialty_group_id)
          sectionId = grp?.section_id
        }
        const section = sections.find(s => s.id === sectionId)
        const g = {
          specialtyId: sc.specialty_id ?? 'none',
          specialtyName: sc.specialty_name || spec?.label_fr || '—',
          specialtyCode: sc.specialty_code || spec?.code || '',
          subsystem: section ? getSectionSubsystem(section) : null,
          rows: []
        }
        map.set(key, g)
        groups.push(g)
      }
      map.get(key)!.rows.push(sc)
    }
    return groups
  }, [competencyFilteredSubjectSpecialties, specialties, levels, cycles, specialtyGroups, sections])

  // Specialites disponibles pour le filtre (cascade niveau -> cycle -> groupes)

  // ❌ SUPPRIMER l'ancien bloc pagination qui était ici :
  // const competencyTotalPages = ...
  // const competencyFilteredSubjectSpecialties = useMemo(...)

  // Specialites disponibles pour le filtre (cascade niveau -> cycle -> groupes)
  const competencyFilterSpecialties = useMemo(() => {
    let groupIds: number[] | null = null
    if (competencyFilterLevel) {
      const lvl = levels.find(l => l.id === Number(competencyFilterLevel))
      const cyc = lvl ? cycles.find(c => c.id === lvl.cycle_id) : undefined
      const gs = cyc ? specialtyGroups.filter(g => g.cycle_id === cyc.id || g.section_id === cyc.section_id) : []
      groupIds = gs.map(g => g.id)
    } else if (competencyFilterCycle) {
      const cyc = cycles.find(c => c.id === Number(competencyFilterCycle))
      const gs = cyc ? specialtyGroups.filter(g => g.cycle_id === cyc.id || g.section_id === cyc.section_id) : []
      groupIds = gs.map(g => g.id)
    }
    if (groupIds === null) return specialties
    return specialties.filter(s => groupIds!.includes(s.specialty_group_id))
  }, [specialties, specialtyGroups, levels, cycles, competencyFilterLevel, competencyFilterCycle])

  const getCompetencyCount = (subjectSpecialtyId: number, trimester?: number) => {
    return competencies.filter(c => c.subject_specialty_id === subjectSpecialtyId && (!trimester || c.trimester === trimester)).length
  }

  const totalClasses = classes.filter(c => !userSchoolId || getClassSchoolId(c, levels, cycles, sections) === userSchoolId).length
  const openClasses = classes.filter(c => c.status === 'open' && (!userSchoolId || getClassSchoolId(c, levels, cycles, sections) === userSchoolId)).length
  const closedClasses = classes.filter(c => c.status === 'closed' && (!userSchoolId || getClassSchoolId(c, levels, cycles, sections) === userSchoolId)).length
  const hasSections = sections.length > 0
  const hasCycles = cycles.length > 0
  const hasLevels = levels.length > 0

  // ============================================================================
  // SORT HANDLER
  // ============================================================================

  const handleSubjectSort = (field: SortField) => {
    if (subjectSortField === field) {
      setSubjectSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSubjectSortField(field)
      setSubjectSortDir('asc')
    }
  }

  // ============================================================================
  // EXPORTS
  // ============================================================================

  const getExportData = useCallback(() => {
    return filteredClasses.map(c => {
      const d = getClassDisplayData(c)
      return {
        [lang === 'fr' ? 'Section' : 'Section']: d.sectionName,
        [lang === 'fr' ? 'Cycle' : 'Cycle']: d.cycleName,
        [lang === 'fr' ? 'Specialite' : 'Specialty']: d.specialtyName,
        [lang === 'fr' ? 'Niveau' : 'Level']: d.levelName,
        [lang === 'fr' ? 'Classe' : 'Class']: c.name,
        [lang === 'fr' ? 'Abreviation' : 'Abbreviation']: c.abbreviation,
        [lang === 'fr' ? 'Statut' : 'Status']: c.status === 'open' ? (lang === 'fr' ? 'Ouverte' : 'Open') : (lang === 'fr' ? 'Fermee' : 'Closed'),
        [lang === 'fr' ? 'Capacite' : 'Capacity']: c.capacity
      }
    })
  }, [filteredClasses, getClassDisplayData, lang])

  const exportToCSV = useCallback(() => {
    const data = getExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const headers = Object.keys(data[0])
    const csvRows = [headers.map(escapeCSV).join(';'), ...data.map(row => headers.map(h => escapeCSV(row[h as keyof typeof row] as string)).join(';'))]
    const csvContent = '\ufeff' + csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `classes_${getFormattedDate()}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
    toast.success(txt.csvExported)
  }, [getExportData, txt])

  const exportToExcel = useCallback(() => {
    const data = getExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const title = lang === 'fr' ? 'Liste des Classes' : 'Class List'
    const headers = Object.keys(data[0])
    const workbook = new ExcelJS.Workbook()
    const ws = workbook.addWorksheet(lang === 'fr' ? 'Classes' : 'Classes')
    ws.mergeCells(1, 1, 1, headers.length)
    const titleCell = ws.getCell(1, 1)
    titleCell.value = title; titleCell.font = { bold: true, size: 16, color: { argb: 'FF1E40AF' } }; titleCell.alignment = { horizontal: 'left', vertical: 'middle' }
    const hr = 3
    const headerRow = ws.getRow(hr)
    headers.forEach((h, i) => { const cell = headerRow.getCell(i + 1); cell.value = h; cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } }; cell.alignment = { horizontal: 'center', vertical: 'middle' } })
    headerRow.commit()
    let ri = hr + 1
    data.forEach(row => { const r = ws.getRow(ri); headers.forEach((h, i) => { r.getCell(i + 1).value = row[h as keyof typeof row] as string | number }); r.commit(); ri++ })
    ri++; ws.getRow(ri).getCell(1).value = txt.total; ws.getRow(ri).getCell(2).value = filteredClasses.length; ws.getRow(ri).commit()
    ri++; ws.getRow(ri).getCell(1).value = txt.open; ws.getRow(ri).getCell(2).value = openClasses; ws.getRow(ri).commit()
    ri++; ws.getRow(ri).getCell(1).value = txt.closed; ws.getRow(ri).getCell(2).value = closedClasses; ws.getRow(ri).commit()
    ws.columns = headers.map(() => ({ width: 25 }))
    workbook.xlsx.writeBuffer().then((buf) => {
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `classes_${getFormattedDate()}.xlsx`; a.click(); window.URL.revokeObjectURL(url)
      toast.success(txt.excelExported)
    }).catch(() => { toast.error(txt.excelExportError) })
  }, [getExportData, lang, txt, filteredClasses.length, openClasses, closedClasses])

  const exportToPDF = useCallback(() => {
    const data = getExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
    const pw = doc.internal.pageSize.getWidth()
    doc.setFontSize(18); doc.setTextColor(30, 64, 175)
    doc.text(lang === 'fr' ? 'Liste des Classes' : 'Class List', pw / 2, 15, { align: 'center' })
    doc.setFontSize(10); doc.setTextColor(100, 100, 100)
    doc.text(`${txt.generatedOn}: ${new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}`, pw / 2, 22, { align: 'center' })
    const headers = Object.keys(data[0])
    const body = data.map(row => headers.map(h => String(row[h as keyof typeof row])))
    autoTable(doc, { head: [headers], body, startY: 28, theme: 'grid', headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 10 }, bodyStyles: { fontSize: 9, halign: 'left' }, alternateRowStyles: { fillColor: [239, 246, 255] }, columnStyles: { 0: { halign: 'center' }, 6: { halign: 'center' }, 7: { halign: 'center' } }, margin: { top: 28, left: 10, right: 10, bottom: 20 }, didDrawPage: (d: any) => { doc.setFontSize(8); doc.setTextColor(150, 150, 150); doc.text(`${lang === 'fr' ? 'Page' : 'Page'} ${d.pageNumber} / ${doc.getNumberOfPages()}`, pw / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' }) } })
    const fy = (doc as any).lastAutoTable?.finalY || 50
    doc.setFontSize(11); doc.setTextColor(30, 64, 175); doc.text(txt.summary, 14, fy + 10)
    doc.setFontSize(10); doc.setTextColor(60, 60, 60)
    doc.text(`${txt.totalClasses}: ${filteredClasses.length}`, 14, fy + 17)
    doc.text(`${txt.open}: ${openClasses}`, 70, fy + 17)
    doc.text(`${txt.closed}: ${closedClasses}`, 120, fy + 17)
    doc.save(`classes_${getFormattedDate()}.pdf`)
    toast.success(txt.pdfExported)
  }, [getExportData, lang, txt, filteredClasses.length, openClasses, closedClasses])

  const handleExport = useCallback((format: 'excel' | 'csv' | 'pdf') => {
    switch (format) { case 'csv': exportToCSV(); break; case 'excel': exportToExcel(); break; case 'pdf': exportToPDF(); break }
  }, [exportToCSV, exportToExcel, exportToPDF])

  // Subjects export
  const getSubjectExportData = useCallback(() => {
    return filteredSubjects.map(s => ({
      [lang === 'fr' ? 'Code' : 'Code']: s.code,
      [lang === 'fr' ? 'Libelle FR' : 'Label FR']: s.label_fr,
      [lang === 'fr' ? 'Libelle EN' : 'Label EN']: s.label_en || '',
      [lang === 'fr' ? 'Abreviation' : 'Abbreviation']: s.abbreviation || '',
      [lang === 'fr' ? 'Groupe' : 'Group']: s.group_name || '—',
      [lang === 'fr' ? 'Section' : 'Section']: s.section_name || '—',
      [lang === 'fr' ? 'Specialite' : 'Specialty']: s.specialty_name || '—',
      [lang === 'fr' ? 'Statut' : 'Status']: s.status === 'ACTIVE' ? (lang === 'fr' ? 'Active' : 'Active') : (lang === 'fr' ? 'Inactive' : 'Inactive'),
    }))
  }, [filteredSubjects, lang])

  const exportSubjectsToCSV = useCallback(() => {
    const data = getSubjectExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const headers = Object.keys(data[0])
    const csvRows = [headers.map(escapeCSV).join(';'), ...data.map(row => headers.map(h => escapeCSV(row[h as keyof typeof row] as string)).join(';'))]
    const csvContent = '\ufeff' + csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `matieres_${getFormattedDate()}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
    toast.success(txt.csvExported)
  }, [getSubjectExportData, txt])

  const exportSubjectsToExcel = useCallback(() => {
    const data = getSubjectExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const title = lang === 'fr' ? 'Liste des Matieres' : 'Subject List'
    const headers = Object.keys(data[0])
    const workbook = new ExcelJS.Workbook()
    const ws = workbook.addWorksheet(lang === 'fr' ? 'Matieres' : 'Subjects')
    ws.mergeCells(1, 1, 1, headers.length)
    const titleCell = ws.getCell(1, 1)
    titleCell.value = title; titleCell.font = { bold: true, size: 16, color: { argb: 'FF7C3AED' } }; titleCell.alignment = { horizontal: 'left', vertical: 'middle' }
    const hr = 3
    const headerRow = ws.getRow(hr)
    headers.forEach((h, i) => { const cell = headerRow.getCell(i + 1); cell.value = h; cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7C3AED' } }; cell.alignment = { horizontal: 'center', vertical: 'middle' } })
    headerRow.commit()
    let ri = hr + 1
    data.forEach(row => { const r = ws.getRow(ri); headers.forEach((h, i) => { r.getCell(i + 1).value = row[h as keyof typeof row] as string | number }); r.commit(); ri++ })
    ri++; ws.getRow(ri).getCell(1).value = txt.total; ws.getRow(ri).getCell(2).value = filteredSubjects.length; ws.getRow(ri).commit()
    ri++; ws.getRow(ri).getCell(1).value = txt.active; ws.getRow(ri).getCell(2).value = filteredSubjects.filter(s => s.status === 'ACTIVE').length; ws.getRow(ri).commit()
    ri++; ws.getRow(ri).getCell(1).value = txt.inactive; ws.getRow(ri).getCell(2).value = filteredSubjects.filter(s => s.status === 'INACTIVE').length; ws.getRow(ri).commit()
    ws.columns = headers.map(() => ({ width: 25 }))
    workbook.xlsx.writeBuffer().then((buf) => {
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `matieres_${getFormattedDate()}.xlsx`; a.click(); window.URL.revokeObjectURL(url)
      toast.success(txt.excelExported)
    }).catch(() => { toast.error(txt.excelExportError) })
  }, [getSubjectExportData, lang, txt, filteredSubjects])

  const exportSubjectsToPDF = useCallback(() => {
    const data = getSubjectExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
    const pw = doc.internal.pageSize.getWidth()
    doc.setFontSize(18); doc.setTextColor(124, 58, 237)
    doc.text(lang === 'fr' ? 'Liste des Matieres' : 'Subject List', pw / 2, 15, { align: 'center' })
    doc.setFontSize(10); doc.setTextColor(100, 100, 100)
    doc.text(`${txt.generatedOn}: ${new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}`, pw / 2, 22, { align: 'center' })
    const headers = Object.keys(data[0])
    const body = data.map(row => headers.map(h => String(row[h as keyof typeof row])))
    autoTable(doc, { head: [headers], body, startY: 28, theme: 'grid', headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 10 }, bodyStyles: { fontSize: 9, halign: 'left' }, alternateRowStyles: { fillColor: [245, 243, 255] }, margin: { top: 28, left: 10, right: 10, bottom: 20 }, didDrawPage: (d: any) => { doc.setFontSize(8); doc.setTextColor(150, 150, 150); doc.text(`${lang === 'fr' ? 'Page' : 'Page'} ${d.pageNumber} / ${doc.getNumberOfPages()}`, pw / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' }) } })
    const fy = (doc as any).lastAutoTable?.finalY || 50
    doc.setFontSize(11); doc.setTextColor(124, 58, 237); doc.text(txt.summary, 14, fy + 10)
    doc.setFontSize(10); doc.setTextColor(60, 60, 60)
    doc.text(`${txt.total}: ${filteredSubjects.length}`, 14, fy + 17)
    doc.text(`${txt.active}: ${filteredSubjects.filter(s => s.status === 'ACTIVE').length}`, 70, fy + 17)
    doc.text(`${txt.inactive}: ${filteredSubjects.filter(s => s.status === 'INACTIVE').length}`, 120, fy + 17)
    doc.save(`matieres_${getFormattedDate()}.pdf`)
    toast.success(txt.pdfExported)
  }, [getSubjectExportData, lang, txt, filteredSubjects])

  const handleSubjectExport = useCallback((format: 'excel' | 'csv' | 'pdf') => {
    switch (format) { case 'csv': exportSubjectsToCSV(); break; case 'excel': exportSubjectsToExcel(); break; case 'pdf': exportSubjectsToPDF(); break }
  }, [exportSubjectsToCSV, exportSubjectsToExcel, exportSubjectsToPDF])

  // ==================== EXPORT: MATIERES PAR NIVEAU / SPECIALITE ====================

  const getSubjectClassExportData = useCallback(() => {
    return filteredSubjectSpecialties.map(sc => {
      const subject = subjects.find(s => s.id === sc.subject_id)
      const specialty = specialties.find(s => s.id === sc.specialty_id)
      const level = levels.find(l => l.id === sc.level_id)
      const apName = subject?.teacher_name || (subject?.teacher_id ? teachers.find(t => t.id === subject.teacher_id)?.full_name : '') || ''
      return {
        [lang === 'fr' ? 'Specialite' : 'Specialty']: sc.specialty_name || specialty?.label_fr || '—',
        [lang === 'fr' ? 'Code specialite' : 'Specialty code']: sc.specialty_code || specialty?.code || '',
        [lang === 'fr' ? 'Niveau' : 'Level']: sc.level_name_fr || level?.label_fr || '—',
        [lang === 'fr' ? 'Code matiere' : 'Subject code']: sc.subject_code || subject?.code || '—',
        [lang === 'fr' ? 'Matiere' : 'Subject']: sc.subject_label_fr || subject?.label_fr || '',
        [lang === 'fr' ? 'Coefficient' : 'Coefficient']: sc.coefficient ?? '',
        [lang === 'fr' ? 'Heures/sem' : 'Hours/week']: sc.hours_per_week ?? '',
        [lang === 'fr' ? 'AP (Animateur Pedagogique)' : 'AP (Pedagogical Animator)']: apName || (lang === 'fr' ? 'Sans AP' : 'No AP'),
        [lang === 'fr' ? 'Statut' : 'Status']: sc.status === 'ACTIVE' ? (lang === 'fr' ? 'Active' : 'Active') : (lang === 'fr' ? 'Inactive' : 'Inactive'),
      }
    })
  }, [filteredSubjectSpecialties, subjects, specialties, levels, teachers, lang])

  const exportSubjectClassesToCSV = useCallback(() => {
    const data = getSubjectClassExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const headers = Object.keys(data[0])
    const csvRows = [headers.map(escapeCSV).join(';'), ...data.map(row => headers.map(h => escapeCSV(row[h as keyof typeof row] as string)).join(';'))]
    const csvContent = '\ufeff' + csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `matieres_par_specialite_${getFormattedDate()}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
    toast.success(txt.csvExported)
  }, [getSubjectClassExportData, txt])

  const exportSubjectClassesToExcel = useCallback(() => {
    const data = getSubjectClassExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const title = txt.subjectClassExportTitle
    const headers = Object.keys(data[0])
    const workbook = new ExcelJS.Workbook()
    const ws = workbook.addWorksheet(lang === 'fr' ? 'Matieres' : 'Subjects')
    ws.mergeCells(1, 1, 1, headers.length)
    const titleCell = ws.getCell(1, 1)
    titleCell.value = title; titleCell.font = { bold: true, size: 16, color: { argb: 'FF4F46E5' } }; titleCell.alignment = { horizontal: 'left', vertical: 'middle' }
    const hr = 3
    const headerRow = ws.getRow(hr)
    headers.forEach((h, i) => { const cell = headerRow.getCell(i + 1); cell.value = h; cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } }; cell.alignment = { horizontal: 'center', vertical: 'middle' } })
    headerRow.commit()
    let ri = hr + 1
    data.forEach(row => { const r = ws.getRow(ri); headers.forEach((h, i) => { r.getCell(i + 1).value = row[h as keyof typeof row] as string | number }); r.commit(); ri++ })
    ri++; ws.getRow(ri).getCell(1).value = txt.total; ws.getRow(ri).getCell(2).value = filteredSubjectSpecialties.length; ws.getRow(ri).commit()
    ri++; ws.getRow(ri).getCell(1).value = txt.active; ws.getRow(ri).getCell(2).value = filteredSubjectSpecialties.filter(s => s.status === 'ACTIVE').length; ws.getRow(ri).commit()
    ri++; ws.getRow(ri).getCell(1).value = txt.inactive; ws.getRow(ri).getCell(2).value = filteredSubjectSpecialties.filter(s => s.status === 'INACTIVE').length; ws.getRow(ri).commit()
    ws.columns = headers.map(() => ({ width: 25 }))
    workbook.xlsx.writeBuffer().then((buf) => {
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `matieres_par_specialite_${getFormattedDate()}.xlsx`; a.click(); window.URL.revokeObjectURL(url)
      toast.success(txt.excelExported)
    }).catch(() => { toast.error(txt.excelExportError) })
  }, [getSubjectClassExportData, lang, txt, filteredSubjectSpecialties])

  const exportSubjectClassesToPDF = useCallback(() => {
    const data = getSubjectClassExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
    const pw = doc.internal.pageSize.getWidth()
    doc.setFontSize(18); doc.setTextColor(79, 70, 229)
    doc.text(txt.subjectClassExportTitle, pw / 2, 15, { align: 'center' })
    doc.setFontSize(10); doc.setTextColor(100, 100, 100)
    doc.text(`${txt.generatedOn}: ${new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}`, pw / 2, 22, { align: 'center' })
    const headers = Object.keys(data[0])
    const body = data.map(row => headers.map(h => String(row[h as keyof typeof row])))
    autoTable(doc, { head: [headers], body, startY: 28, theme: 'grid', headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 9 }, bodyStyles: { fontSize: 8, halign: 'left' }, alternateRowStyles: { fillColor: [238, 242, 255] }, columnStyles: { 3: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center' } }, margin: { top: 28, left: 8, right: 8, bottom: 20 }, didDrawPage: (d: any) => { doc.setFontSize(8); doc.setTextColor(150, 150, 150); doc.text(`${lang === 'fr' ? 'Page' : 'Page'} ${d.pageNumber} / ${doc.getNumberOfPages()}`, pw / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' }) } })
    const fy = (doc as any).lastAutoTable?.finalY || 50
    doc.setFontSize(11); doc.setTextColor(79, 70, 229); doc.text(txt.summary, 14, fy + 10)
    doc.setFontSize(10); doc.setTextColor(60, 60, 60)
    doc.text(`${txt.total}: ${filteredSubjectSpecialties.length}`, 14, fy + 17)
    doc.text(`${txt.active}: ${filteredSubjectSpecialties.filter(s => s.status === 'ACTIVE').length}`, 70, fy + 17)
    doc.text(`${txt.inactive}: ${filteredSubjectSpecialties.filter(s => s.status === 'INACTIVE').length}`, 120, fy + 17)
    doc.save(`matieres_par_specialite_${getFormattedDate()}.pdf`)
    toast.success(txt.pdfExported)
  }, [getSubjectClassExportData, lang, txt, filteredSubjectSpecialties])

  const handleSubjectClassExport = useCallback((format: 'excel' | 'csv' | 'pdf') => {
    switch (format) { case 'csv': exportSubjectClassesToCSV(); break; case 'excel': exportSubjectClassesToExcel(); break; case 'pdf': exportSubjectClassesToPDF(); break }
  }, [exportSubjectClassesToCSV, exportSubjectClassesToExcel, exportSubjectClassesToPDF])

  // ==================== EXPORT: COMPETENCES ====================

  const getCompetencyExportData = useCallback(() => {
    return competencyFilteredSubjectSpecialtiesAll.map(sc => {
      const subject = subjects.find(s => s.id === sc.subject_id)
      const specialty = specialties.find(s => s.id === sc.specialty_id)
      const level = levels.find(l => l.id === sc.level_id)
      const rowCycle = level ? cycles.find(c => c.id === level.cycle_id) : undefined
      let rowSectionId: number | undefined = rowCycle?.section_id
      if (!rowSectionId && specialty) {
        const grp = specialtyGroups.find(g => g.id === specialty.specialty_group_id)
        rowSectionId = grp?.section_id
      }
      const rowSection = sections.find(s => s.id === rowSectionId)
      const rowSubsystem = rowSection ? getSectionSubsystem(rowSection) : null
      const getComps = (trimester: number) =>
        competencies
          .filter(c => c.subject_specialty_id === sc.id && c.trimester === trimester)
          .sort((a, b) => a.number - b.number)
          .map(c => `${c.number}) ${c.abbreviation ? '[' + c.abbreviation + '] ' : ''}${c.label_fr}${c.label_en ? ' / ' + c.label_en : ''}`)
          .join('\n')
      return {
        [lang === 'fr' ? 'Specialite' : 'Specialty']: sc.specialty_name || specialty?.label_fr || '—',
        [lang === 'fr' ? 'Niveau' : 'Level']: sc.level_name_fr || level?.label_fr || '—',
        [lang === 'fr' ? 'Section' : 'Section']: rowSubsystem ? (rowSubsystem === 'francophone' ? txt.francophoneSubsystem : txt.anglophoneSubsystem) : '',
        [lang === 'fr' ? 'Matiere' : 'Subject']: `${sc.subject_code || subject?.code || '—'} — ${sc.subject_label_fr || subject?.label_fr || ''}`,
        T1: getComps(1),
        T2: getComps(2),
        T3: getComps(3),
      }
    })
  }, [competencyFilteredSubjectSpecialtiesAll, subjects, specialties, levels, cycles, specialtyGroups, sections, competencies, lang, txt])

  const exportCompetenciesToCSV = useCallback(() => {
    const data = getCompetencyExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const headers = Object.keys(data[0])
    const csvRows = [headers.map(escapeCSV).join(';'), ...data.map(row => headers.map(h => escapeCSV(row[h as keyof typeof row] as string)).join(';'))]
    const csvContent = '\ufeff' + csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `competences_${getFormattedDate()}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
    toast.success(txt.csvExported)
  }, [getCompetencyExportData, txt])

  const exportCompetenciesToExcel = useCallback(() => {
    const data = getCompetencyExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const title = txt.competencyExportTitle
    const headers = Object.keys(data[0])
    const workbook = new ExcelJS.Workbook()
    const ws = workbook.addWorksheet(lang === 'fr' ? 'Competences' : 'Competencies')
    ws.mergeCells(1, 1, 1, headers.length)
    const titleCell = ws.getCell(1, 1)
    titleCell.value = title; titleCell.font = { bold: true, size: 16, color: { argb: 'FF059669' } }; titleCell.alignment = { horizontal: 'left', vertical: 'middle' }
    const hr = 3
    const headerRow = ws.getRow(hr)
    headers.forEach((h, i) => { const cell = headerRow.getCell(i + 1); cell.value = h; cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } }; cell.alignment = { horizontal: 'center', vertical: 'middle' } })
    headerRow.commit()
    let ri = hr + 1
    data.forEach(row => {
      const r = ws.getRow(ri)
      headers.forEach((h, i) => {
        const cell = r.getCell(i + 1)
        cell.value = row[h as keyof typeof row] as string | number
        cell.alignment = { wrapText: true, vertical: 'top' }
      })
      r.height = 45
      r.commit(); ri++
    })
    ri++; ws.getRow(ri).getCell(1).value = txt.total; ws.getRow(ri).getCell(2).value = competencyFilteredSubjectSpecialtiesAll.length; ws.getRow(ri).commit()
    ri++; ws.getRow(ri).getCell(1).value = txt.competenciesConfigured; ws.getRow(ri).getCell(2).value = competencies.length; ws.getRow(ri).commit()
    ws.columns = [
      { width: 28 }, { width: 18 }, { width: 14 }, { width: 35 },
      { width: 45 }, { width: 45 }, { width: 45 },
    ]
    workbook.xlsx.writeBuffer().then((buf) => {
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `competences_${getFormattedDate()}.xlsx`; a.click(); window.URL.revokeObjectURL(url)
      toast.success(txt.excelExported)
    }).catch(() => { toast.error(txt.excelExportError) })
  }, [getCompetencyExportData, lang, txt, competencyFilteredSubjectSpecialtiesAll, competencies])

  const exportCompetenciesToPDF = useCallback(() => {
    const data = getCompetencyExportData()
    if (data.length === 0) { toast.info(txt.noDataExport); return }
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
    const pw = doc.internal.pageSize.getWidth()
    doc.setFontSize(18); doc.setTextColor(5, 150, 105)
    doc.text(txt.competencyExportTitle, pw / 2, 15, { align: 'center' })
    doc.setFontSize(10); doc.setTextColor(100, 100, 100)
    doc.text(`${txt.generatedOn}: ${new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}`, pw / 2, 22, { align: 'center' })
    const headers = Object.keys(data[0])
    const body = data.map(row => headers.map(h => String(row[h as keyof typeof row])))
    autoTable(doc, { head: [headers], body, startY: 28, theme: 'grid', headStyles: { fillColor: [5, 150, 105], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 9 }, bodyStyles: { fontSize: 7, halign: 'left', valign: 'top' }, alternateRowStyles: { fillColor: [236, 253, 245] }, margin: { top: 28, left: 8, right: 8, bottom: 20 }, didDrawPage: (d: any) => { doc.setFontSize(8); doc.setTextColor(150, 150, 150); doc.text(`${lang === 'fr' ? 'Page' : 'Page'} ${d.pageNumber} / ${doc.getNumberOfPages()}`, pw / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' }) } })
    const fy = (doc as any).lastAutoTable?.finalY || 50
    doc.setFontSize(11); doc.setTextColor(5, 150, 105); doc.text(txt.summary, 14, fy + 10)
    doc.setFontSize(10); doc.setTextColor(60, 60, 60)
    doc.text(`${txt.total}: ${competencyFilteredSubjectSpecialtiesAll.length}`, 14, fy + 17)
    doc.text(`${txt.competenciesConfigured}: ${competencies.length}`, 70, fy + 17)
    doc.save(`competences_${getFormattedDate()}.pdf`)
    toast.success(txt.pdfExported)
  }, [getCompetencyExportData, lang, txt, competencyFilteredSubjectSpecialtiesAll, competencies])

  const handleCompetencyExport = useCallback((format: 'excel' | 'csv' | 'pdf') => {
    switch (format) { case 'csv': exportCompetenciesToCSV(); break; case 'excel': exportCompetenciesToExcel(); break; case 'pdf': exportCompetenciesToPDF(); break }
  }, [exportCompetenciesToCSV, exportCompetenciesToExcel, exportCompetenciesToPDF])

  // ==================== TEMPLATE + IMPORT COMPETENCES ====================

  const downloadCompetencyTemplate = useCallback(async () => {
    // TOUTES les attributions (tous niveaux / specialites) — le fichier Excel
    // genere hors de l'app (modele_competences) sera importe ici.
    const targets = subjectSpecialties.filter(sc =>
      (!userSchoolId || sc.school_id === userSchoolId || sc.school_id === null || sc.school_id === undefined)
    )
    const workbook = new ExcelJS.Workbook()
    const ws = workbook.addWorksheet('Competences')
    ws.mergeCells(1, 1, 1, 8)
    const titleCell = ws.getCell(1, 1)
    titleCell.value = txt.templateTitle
    titleCell.font = { bold: true, size: 14, color: { argb: 'FF059669' } }
    titleCell.alignment = { horizontal: 'left', vertical: 'middle' }
    ws.mergeCells(2, 1, 2, 8)
    const instrCell = ws.getCell(2, 1)
    instrCell.value = txt.templateInstruction
    instrCell.font = { size: 10, color: { argb: 'FF6B7280' }, italic: true }
    instrCell.alignment = { wrapText: true, vertical: 'middle' }
    ws.getRow(2).height = 30
    const hr = 4
    const headers = ['Code matiere', 'Specialite', 'Niveau', 'Trimestre (1-3)', 'Numero (1-6)', 'Libelle FR *', 'Abreviation', 'Libelle EN', 'Description']
    const headerRow = ws.getRow(hr)
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1)
      cell.value = h
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } }
      cell.alignment = { horizontal: 'center', vertical: 'middle' }
    })
    headerRow.commit()
    // Pre-remplissage : pour chaque attribution, 15 lignes (3 trimestres x 5 numeros)
    let ri = hr + 1
    targets.forEach(sc => {
      const subject = subjects.find(s => s.id === sc.subject_id)
      const specialty = specialties.find(s => s.id === sc.specialty_id)
      const level = levels.find(l => l.id === sc.level_id)
      const code = sc.subject_code || subject?.code || ''
      const levelName = sc.level_name_fr || level?.label_fr || ''
      const specName = sc.specialty_name || specialty?.label_fr || ''
      for (let t = 1; t <= 3; t++) {
        for (let n = 1; n <= 6; n++) {
          const r = ws.getRow(ri)
          r.getCell(1).value = code
          r.getCell(2).value = specName
          r.getCell(3).value = levelName
          r.getCell(4).value = t
          r.getCell(5).value = n
          r.commit()
          ri++
        }
      }
    })
    ws.columns = [{ width: 16 }, { width: 20 }, { width: 16 }, { width: 16 }, { width: 15 }, { width: 40 }, { width: 14 }, { width: 40 }, { width: 45 }]
    try {
      const buf = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `modele_competences_${getFormattedDate()}.xlsx`; a.click(); window.URL.revokeObjectURL(url)
      toast.success(txt.excelExported)
    } catch { toast.error(txt.excelExportError) }
  }, [subjectSpecialties, subjects, specialties, levels, userSchoolId, txt])

  const handleCompetencyImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setImportingCompetencies(true)

    // Normalisation accent-insensible
    const normalizeStr = (s: any) => String(s ?? '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().trim()

    // Lecture robuste d'une cellule (richText, formule, objet)
    const cellText = (row: ExcelJS.Row, col?: number): string => {
      if (!col) return ''
      const v: any = row.getCell(col).value
      if (v == null) return ''
      if (typeof v === 'object') {
        if (v.richText) return v.richText.map((r: any) => r.text).join('')
        if (v.text) return String(v.text)
        if (v.result != null) return String(v.result)
        return ''
      }
      return String(v)
    }

    try {
      const buffer = await file.arrayBuffer()
      const wb = new ExcelJS.Workbook()
      await wb.xlsx.load(buffer as any)
      const ws = wb.worksheets[0]
      if (!ws) throw new Error('empty')

      // ── Localiser la ligne d'en-tête (accent-insensible) ──
      let headerRowIdx = 0
      const colMap: Record<string, number> = {}
      ws.eachRow((row, n) => {
        if (headerRowIdx) return
        row.eachCell((cell, cn) => {
          const v = normalizeStr(cell.value)
          if (v.includes('code') && v.includes('matiere')) colMap.code = cn
          else if (v.includes('specialite')) colMap.specialty = cn
          else if (v.includes('niveau') || v.includes('level')) colMap.level = cn
          else if (v.includes('trimestre')) colMap.trimester = cn
          else if ((v.includes('numero') || v.includes('number') || v.includes('n°') || v === 'n' || v === 'no') && !v.includes('libelle') && !v.includes('trimestre')) colMap.number = cn
          else if (v.includes('abrev')) colMap.abbr = cn
          else if (v.includes('libelle') && v.includes('fr')) colMap.fr = cn
          else if (v.includes('libelle') && v.includes('en')) colMap.en = cn
          else if (v.includes('description') || v.includes('desc')) colMap.desc = cn
        })
        if (colMap.trimester && colMap.fr) headerRowIdx = n
      })
      if (!headerRowIdx) throw new Error('header')

      // ── Diagnostic ──
      const existingKeys = new Set(competencies.map(c => `${c.subject_specialty_id}-${c.trimester}-${c.number}`))
      const unknownCodes = new Set<string>()
      const unmatchedLevels = new Set<string>()
      const unmatchedSpecialties = new Set<string>()
      let emptyLabel = 0
      let invalidTN = 0

      const items: any[] = []
      const seen = new Set<string>()
      let skippedExisting = 0

      ws.eachRow((row, n) => {
        if (n <= headerRowIdx) return
        const code = cellText(row, colMap.code || 1).toUpperCase()
        const niveau = normalizeStr(cellText(row, colMap.level))
        const specName = normalizeStr(cellText(row, colMap.specialty))
        const trimester = Math.round(Number(cellText(row, colMap.trimester)))
        const number = Math.round(Number(cellText(row, colMap.number || (colMap.trimester ? colMap.trimester + 1 : 0))))
        const label_fr = cellText(row, colMap.fr).trim()
        const abbreviation = cellText(row, colMap.abbr).toUpperCase()
        const label_en = cellText(row, colMap.en).trim()
        const description = cellText(row, colMap.desc).trim()

        if (!label_fr) { emptyLabel++; return }
        if (!code) { unknownCodes.add('(vide)'); return }
        if (![1, 2, 3].includes(trimester) || ![1, 2, 3, 4, 5, 6].includes(number)) { invalidTN++; return }

        let subject = subjects.find(s => (s.code || '').toUpperCase() === code)
        if (!subject) {
          // ✅ Repli : le fichier peut contenir le NOM complet au lieu du code
          // (ex "LVII - ALLEMAND" dans le fichier vs code "ALL" en base)
          const nv = normalizeStr(code)
          subject = subjects.find(s =>
            [s.label_fr, s.label_en, (s as any).name].filter(Boolean)
              .map(normalizeStr)
              .some(nm => nm === nv || nm.includes(nv) || nv.includes(nm))
          )
        }
        if (!subject) { unknownCodes.add(code); return }

        // Attributions de cette matiere (tous niveaux/specialites)
        let targets = subjectSpecialties.filter(sc => sc.subject_id === subject.id)

        // Filtre NIVEAU tolerant (accent-insensible, code ou libelle)
        if (niveau) {
          const before = targets.length
          targets = targets.filter(sc => {
            const lvl = levels.find(l => l.id === sc.level_id)
            const names = [sc.level_name_fr, lvl?.label_fr, lvl?.label_en, sc.level_code, lvl?.code]
              .filter(Boolean).map(normalizeStr)
            return names.some(nm => nm === niveau || nm.includes(niveau) || niveau.includes(nm))
          })
          if (targets.length === 0 && before > 0) { unmatchedLevels.add(cellText(row, colMap.level)); return }
        }

        // Filtre SPECIALITE tolerant
        if (specName) {
          const before = targets.length
          targets = targets.filter(sc => {
            const sp = specialties.find(s => s.id === sc.specialty_id)
            const names = [sc.specialty_name, sc.specialty_code, sp?.label_fr, sp?.code, sp?.abbreviation]
              .filter(Boolean).map(normalizeStr)
            return names.some(nm => nm === specName || nm.includes(specName) || specName.includes(nm))
          })
          if (targets.length === 0 && before > 0) { unmatchedSpecialties.add(cellText(row, colMap.specialty)); return }
        }

        targets.forEach(sc => {
          const key = `${sc.id}-${trimester}-${number}`
          if (seen.has(key)) return
          seen.add(key)
          if (existingKeys.has(key)) { skippedExisting++; return }
          items.push({
            subject_specialty_id: sc.id,
            trimester, number,
            label_fr,
            label_en: label_en || label_fr,
            abbreviation: abbreviation || null,
            description: description || null,
            status: 'ACTIVE',
            school_id: user?.school_id || 1,
            academic_year_id: selectedAcademicYear ? Number(selectedAcademicYear) : null
          })
        })
      })

      if (items.length === 0) {
        const parts: string[] = []
        if (unknownCodes.size) parts.push(`${txt.competencyImportUnknownCodes}: ${[...unknownCodes].join(', ')}`)
        if (unmatchedLevels.size) parts.push(`${lang === 'fr' ? 'Niveau(x) sans correspondance' : 'Level(s) not found'}: ${[...unmatchedLevels].join(', ')}`)
        if (unmatchedSpecialties.size) parts.push(`${lang === 'fr' ? 'Specialite(s) sans correspondance' : 'Specialty(s) not found'}: ${[...unmatchedSpecialties].join(', ')}`)
        if (emptyLabel) parts.push(`${emptyLabel} ${lang === 'fr' ? 'ligne(s) sans Libelle FR' : 'row(s) without Label FR'}`)
        if (invalidTN) parts.push(`${invalidTN} ${lang === 'fr' ? 'ligne(s) avec Trimestre/Numero invalide' : 'row(s) with invalid Trimester/Number'}`)
        toast.error(parts.length > 0 ? parts.join(' — ') : txt.competencyImportEmpty)
        return
      }

 

            let created = 0
      let skipped = skippedExisting
      const isDuplicateError = (e: any) => {
        const status = e?.response?.status
        const detail = String(e?.response?.data?.detail || '')
        return status === 400 && /existe d[eé]j[aà]/i.test(detail)
      }

      // 1) Endpoint bulk : payload { items }
      let bulkOk = false
      try {
        const res: any = await apiService.post('/competencies/bulk', { items })
        created = typeof res?.created === 'number' ? res.created : items.length
        bulkOk = true
      } catch { /* peu importe l'erreur (500/CORS/network/422) : on essaie le format suivant */ }

      // 2) Endpoint bulk : payload tableau brut (schéma alternatif courant)
      if (!bulkOk) {
        try {
          const res: any = await apiService.post('/competencies/bulk', items)
          created = typeof res?.created === 'number' ? res.created : items.length
          bulkOk = true
        } catch { /* on passe au séquentiel */ }
      }

      // 3) Repli séquentiel : ignore les doublons, COMPTABILISE les autres erreurs SANS JAMAIS abandonner
      let failed = 0
      const failedRows: any[] = []
      if (!bulkOk) {
        for (const it of items) {
          try {
            await apiService.post('/competencies', it)
            created++
          } catch (e: any) {
            if (isDuplicateError(e)) { skipped++ }
            else { failed++; failedRows.push({ code: it.label_fr?.substring(0, 60), trimester: it.trimester, number: it.number, status: e?.response?.status || 'network' }) }
          }
        }
        if (failed > 0) console.error('[Competencies] Lignes en échec (à copier pour moi) :', failedRows)
      }
      let msg = `${created} ${txt.competencyImportSuccess}`
      if (skipped > 0) msg += ` — ${skipped} ${txt.competencyImportSkipped}`
      if (failed > 0) msg += ` — ${failed} ${lang === 'fr' ? 'échec(s) — détails en console F12' : 'failed — see F12 console'}`
      if (unknownCodes.size > 0) msg += ` — ${txt.competencyImportUnknownCodes}: ${[...unknownCodes].slice(0, 8).join(', ')}${unknownCodes.size > 8 ? '…' : ''}`
      if (unmatchedLevels.size > 0) msg += ` — ${lang === 'fr' ? 'Niveau(x) sans correspondance' : 'Level(s) not found'}: ${[...unmatchedLevels].join(', ')}`
      if (unmatchedSpecialties.size > 0) msg += ` — ${lang === 'fr' ? 'Spécialité(s) sans correspondance' : 'Specialty(s) not found'}: ${[...unmatchedSpecialties].join(', ')}`
      toast.success(msg)
      await loadCompetencies()
    } catch (err: any) {
      console.error('[Competencies] Import error:', err)
      toast.error(err?.message === 'header' ? txt.competencyImportEmpty : txt.competencyImportError)
    } finally { setImportingCompetencies(false) }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    )
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-amber-50/20">
      {/* Header */}
      <div className="page-header bg-white/80 backdrop-blur-md border-b border-blue-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
              <School className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="page-title text-2xl font-bold bg-gradient-to-r from-blue-700 to-blue-900 bg-clip-text text-transparent">
                {lang === 'fr' ? 'Niveaux & Classes' : 'Levels & Classes'}
              </h1>
              <p className="page-subtitle text-sm text-gray-500 mt-0.5">
                {activeTab === 'subjects'
                  ? `${subjects.length} ${txt.subjectsCount.toLowerCase()}`
                  : activeTab === 'subject-classes'
                  ? `${subjectSpecialties.length} attributions`
                  : activeTab === 'competencies'
                  ? `${competencies.length} compétences`
                  : `${totalClasses} ${txt.classes.toLowerCase()} — ${openClasses} ${txt.open.toLowerCase()} / ${closedClasses} ${txt.closed.toLowerCase()}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex bg-gray-100 rounded-lg p-1 flex-wrap">
              <button onClick={() => setActiveTab('cycles')} className={cn('px-3 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'cycles' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}><RotateCcw className="w-4 h-4 inline mr-1" />{txt.cycles}</button>
              <button onClick={() => setActiveTab('levels')} className={cn('px-3 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'levels' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}><ArrowUp className="w-4 h-4 inline mr-1" />{txt.levels}</button>
              <button onClick={() => setActiveTab('classes')} className={cn('px-3 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'classes' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}><School className="w-4 h-4 inline mr-1" />{txt.classes}</button>
              <button onClick={() => setActiveTab('subjects')} className={cn('px-3 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'subjects' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}><BookOpen className="w-4 h-4 inline mr-1" />{txt.subjects}</button>
              <button onClick={() => setActiveTab('subject-classes')} className={cn('px-3 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'subject-classes' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}><BookPlus className="w-4 h-4 inline mr-1" />{txt.subjectClasses}</button>
              <button onClick={() => setActiveTab('competencies')} className={cn('px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-1', activeTab === 'competencies' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}><Sparkles className="w-4 h-4 inline" />{txt.competencies}</button>
            </div>

            {activeTab !== 'subjects' && activeTab !== 'subject-classes' && activeTab !== 'competencies' && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={txt.search} className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-64" />
                </div>
                <button onClick={() => setShowFilters(!showFilters)} className={cn('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all', showFilters ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50')}><Filter className="w-4 h-4" />{txt.filters}</button>
              </>
            )}

            {activeTab === 'subjects' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={subjectSearchTerm} onChange={(e) => setSubjectSearchTerm(e.target.value)} placeholder={txt.search} className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 w-64" />
              </div>
            )}

            {activeTab === 'subject-classes' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={scSearchTerm} onChange={(e) => setScSearchTerm(e.target.value)} placeholder={txt.search} className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 w-64" />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {activeTab === 'subjects' ? (
            <>
              <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center"><BookOpen className="w-5 h-5 text-purple-600" /></div><div><p className="text-2xl font-bold text-purple-700">{subjects.filter(s => !userSchoolId || s.school_id === userSchoolId).length}</p><p className="text-xs text-gray-500">{txt.total}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-emerald-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><BookMarked className="w-5 h-5 text-emerald-600" /></div><div><p className="text-2xl font-bold text-emerald-700">{subjects.filter(s => s.status === 'ACTIVE' && (!userSchoolId || s.school_id === userSchoolId)).length}</p><p className="text-xs text-gray-500">{txt.active}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center"><GraduationCap className="w-5 h-5 text-gray-600" /></div><div><p className="text-2xl font-bold text-gray-700">{sections.filter(s => !userSchoolId || s.school_id === userSchoolId).length}</p><p className="text-xs text-gray-500">{txt.section}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-amber-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-amber-600" /></div><div><p className="text-2xl font-bold text-amber-700">{specialties.filter(sp => !userSchoolId || (() => { const g = specialtyGroups.find(gg => gg.id === sp.specialty_group_id); if (!g) return false; const s = sections.find(ss => ss.id === g.section_id); return s ? s.school_id === userSchoolId : false })()).length}</p><p className="text-xs text-gray-500">{txt.specialty}</p></div></div></div>
            </>
          ) : activeTab === 'subject-classes' ? (
            <>
              <div className="bg-white rounded-xl p-4 border border-indigo-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center"><BookPlus className="w-5 h-5 text-indigo-600" /></div><div><p className="text-2xl font-bold text-indigo-700">{subjectSpecialties.filter(sc => !userSchoolId || sc.school_id === userSchoolId).length}</p><p className="text-xs text-gray-500">{txt.total}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-emerald-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><BookMarked className="w-5 h-5 text-emerald-600" /></div><div><p className="text-2xl font-bold text-emerald-700">{subjectSpecialties.filter(sc => sc.status === 'ACTIVE' && (!userSchoolId || sc.school_id === userSchoolId)).length}</p><p className="text-xs text-gray-500">{txt.active}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><BookOpen className="w-5 h-5 text-blue-600" /></div><div><p className="text-2xl font-bold text-blue-700">{subjects.length}</p><p className="text-xs text-gray-500">{txt.subjects}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-amber-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><School className="w-5 h-5 text-amber-600" /></div><div><p className="text-2xl font-bold text-amber-700">{classes.filter(c => !userSchoolId || c.school_id === userSchoolId).length}</p><p className="text-xs text-gray-500">{txt.classes}</p></div></div></div>
            </>
          ) : activeTab === 'competencies' ? (
            <>
              <div className="bg-white rounded-xl p-4 border border-emerald-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><Sparkles className="w-5 h-5 text-emerald-600" /></div><div><p className="text-2xl font-bold text-emerald-700">{competencies.length}</p><p className="text-xs text-gray-500">{txt.total}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><LayoutList className="w-5 h-5 text-blue-600" /></div><div><p className="text-2xl font-bold text-blue-700">{competencyFilteredSubjectSpecialtiesAll.length}</p><p className="text-xs text-gray-500">{txt.subjectClassList}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center"><BookPlus className="w-5 h-5 text-purple-600" /></div><div><p className="text-2xl font-bold text-purple-700">{subjects.length}</p><p className="text-xs text-gray-500">{txt.subjects}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-amber-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Layers className="w-5 h-5 text-amber-600" /></div><div><p className="text-2xl font-bold text-amber-700">{specialties.length}</p><p className="text-xs text-gray-500">{txt.specialty}</p></div></div></div>
            </>
          ) : (
            <>
              <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Layers className="w-5 h-5 text-blue-600" /></div><div><p className="text-2xl font-bold text-blue-700">{totalClasses}</p><p className="text-xs text-gray-500">{txt.total}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-emerald-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><Unlock className="w-5 h-5 text-emerald-600" /></div><div><p className="text-2xl font-bold text-emerald-700">{openClasses}</p><p className="text-xs text-gray-500">{txt.open}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-red-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center"><Lock className="w-5 h-5 text-red-600" /></div><div><p className="text-2xl font-bold text-red-700">{closedClasses}</p><p className="text-xs text-gray-500">{txt.closed}</p></div></div></div>
              <div className="bg-white rounded-xl p-4 border border-amber-100 shadow-sm"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-amber-600" /></div><div><p className="text-2xl font-bold text-amber-700">{levels.length}</p><p className="text-xs text-gray-500">{txt.levelsCount}</p></div></div></div>
            </>
          )}
        </div>

        {/* ============ TAB: COMPETENCIES ============ */}
        {activeTab === 'competencies' && (
          <div className="space-y-6">
            {!canManageSubjects && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700">{txt.readOnly}</span>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-b border-emerald-100">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  {txt.competencyConfig}
                </h2>
                <p className="text-sm text-gray-600 mt-1">{txt.competencyConfigDesc}</p>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.section}</label>
                    <select value={competencyFilterSection} onChange={(e) => { setCompetencyFilterSection(Number(e.target.value) || ''); setCompetencyFilterCycle(''); setCompetencyFilterLevel(''); setCompetencyFilterSpecialty(''); }} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400">
                      <option value="">{txt.all} ({txt.francophoneSubsystem} + {txt.anglophoneSubsystem})</option>
                      {sections.map(s => {
                        const subsystem = getSectionSubsystem(s)
                        return (
                          <option key={s.id} value={s.id}>
                            {lang === 'fr' ? s.label_fr : s.label_en} — {subsystem === 'francophone' ? txt.francophoneSubsystem : txt.anglophoneSubsystem}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.cycle}</label>
                    <select value={competencyFilterCycle} onChange={(e) => { setCompetencyFilterCycle(Number(e.target.value) || ''); setCompetencyFilterLevel(''); setCompetencyFilterSpecialty(''); }} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400">
                      <option value="">{txt.allCycles}</option>
                      {cycles.filter(c => (isFirstCycleLabel(c.label_fr || c.label_en || '') || levels.some(l => l.cycle_id === c.id && /\bform\s*[1-5]\b/i.test(`${l.label_fr} ${l.label_en}`))) && (!competencyFilterSection || c.section_id === Number(competencyFilterSection))).map(c => (
                        <option key={c.id} value={c.id}>{lang === 'fr' ? c.label_fr : c.label_en} — {sections.find(s => s.id === c.section_id)?.label_fr || ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.level}</label>
                    <select value={competencyFilterLevel} onChange={(e) => { setCompetencyFilterLevel(Number(e.target.value) || ''); setCompetencyFilterSpecialty(''); }} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400">
                      <option value="">{txt.allLevels}</option>
                      {levels.filter(l => {
                        const lvlLbl = (l.label_fr || l.label_en || '').toLowerCase()
                        // 1er cycle : libellés FR/EN + niveaux anglophones Form 1 à Form 4
                        const isForm = /\bform\s*[1-5]\s*[ab]?\b/.test(lvlLbl)
                        const isFirst = isFirstCycleLabel(lvlLbl) || isForm
                        return isFirst &&
                          (!competencyFilterCycle || isForm || l.cycle_id === Number(competencyFilterCycle)) &&
                          (!competencyFilterSection || cycles.find(c => c.id === l.cycle_id)?.section_id === Number(competencyFilterSection))
                      }).map(l => (
                        <option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.specialty}</label>
                    <select value={competencyFilterSpecialty} onChange={(e) => setCompetencyFilterSpecialty(Number(e.target.value) || '')} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400">
                      <option value="">{txt.all}</option>
                      {competencyFilterSpecialties.map(s => (
                        <option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.subjectFilter}</label>
                    <select value={competencyFilterSubject} onChange={(e) => setCompetencyFilterSubject(Number(e.target.value) || '')} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400">
                      <option value="">{txt.all}</option>
                      {subjects.map(s => (
                        <option key={s.id} value={s.id}>{s.label_fr} ({s.code})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <LayoutList className="w-5 h-5 text-emerald-600" />
                  {txt.subjectClassList} — {txt.competencyConfig}
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-gray-500">{competencyFilteredSubjectSpecialties.length} {txt.results}</span>
                  {canManageSubjects && (
                    <button onClick={() => { setCompCopySource(''); setCompCopyTarget(''); setCompCopyLevel(competencyFilterLevel || ''); setCompCopyOverwrite(false); setCompCopyModalOpen(true) }} className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors border border-blue-200" title={txt.copyCompetencies}>
                      <Copy className="w-4 h-4" />{txt.copyCompetencies}
                    </button>
                  )}
                  {canManageSubjects && (
                    <button onClick={() => setConfirmDeleteAllGlobal(true)} className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors border border-red-200" title={lang === 'fr' ? 'Supprimer TOUTES les compétences des matières visibles (filtres actuels)' : 'Delete ALL competencies of visible subjects (current filters)'}>
                      <Trash2 className="w-4 h-4" />{lang === 'fr' ? 'Tout effacer' : 'Delete all'}
                    </button>
                  )}
                  <div className="flex items-center gap-1">
                    <button onClick={downloadCompetencyTemplate} className="flex items-center gap-2 px-3 py-2 bg-white text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-50 transition-colors border border-emerald-200" title={txt.downloadTemplate}><FileDown className="w-4 h-4" />{txt.downloadTemplate}</button>
                    <button onClick={() => competencyFileInputRef.current?.click()} disabled={importingCompetencies} className={cn('flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border', importingCompetencies ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800 border-emerald-600')} title={txt.importCompetencies}>
                      {importingCompetencies ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {importingCompetencies ? txt.importingCompetencies : txt.importCompetencies}
                    </button>
                    <input ref={competencyFileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleCompetencyImport} />
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleCompetencyExport('excel')} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors border border-emerald-200" title="Excel"><FileText className="w-4 h-4" />Excel</button>
                    <button onClick={() => handleCompetencyExport('csv')} className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors border border-blue-200" title="CSV"><ArrowDown className="w-4 h-4" />CSV</button>
                    <button onClick={() => handleCompetencyExport('pdf')} className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors border border-red-200" title="PDF"><Printer className="w-4 h-4" />PDF</button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{lang === 'fr' ? 'Lignes par page:' : 'Rows per page:'}</span>
                    <select value={competencyPageSize} onChange={(e) => { setCompetencyPageSize(Number(e.target.value)); setCompetencyCurrentPage(1) }} className="px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-xs text-gray-500">
                      {competencyFilteredSubjectSpecialtiesAll.length} {lang === 'fr' ? 'total' : 'total'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCompetencyCurrentPage(p => Math.max(1, p - 1))} disabled={competencyCurrentPage === 1} className="px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">←</button>
                    <span className="text-xs text-gray-600 px-2">
                      {lang === 'fr' ? 'Page' : 'Page'} {competencyCurrentPage} / {competencyTotalPages}
                    </span>
                    <button onClick={() => setCompetencyCurrentPage(p => Math.min(competencyTotalPages, p + 1))} disabled={competencyCurrentPage === competencyTotalPages} className="px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">→</button>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{txt.subjectFilter}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{txt.level}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{txt.specialty}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-32">{txt.section}</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-24">T1</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-24">T2</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-24">T3</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-32">{txt.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {competencyGroups.map((group) => (
                      <>
                        {/* En-tete de groupe : Specialite */}
                        <tr key={`group-${group.specialtyId}`} className="bg-gradient-to-r from-emerald-50/80 to-emerald-100/40 border-b border-emerald-100">
                          <td colSpan={8} className="py-2.5 px-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Layers className="w-4 h-4 text-emerald-600" />
                                <span className="text-sm font-bold text-emerald-800">{group.specialtyName}</span>
                                {group.specialtyCode && <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded">{group.specialtyCode}</span>}
                                {group.subsystem && (
                                  <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', group.subsystem === 'francophone' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200')}>
                                    {group.subsystem === 'francophone' ? txt.francophoneSubsystem : txt.anglophoneSubsystem}
                                  </span>
                                )}
                                <span className="text-xs text-gray-500">({group.rows.length} {txt.subjectFilter.toLowerCase()})</span>
                              </div>
                              {canManageSubjects && (
                                <button onClick={() => { setCompCopySource(''); setCompCopyTarget(typeof group.specialtyId === 'number' ? group.specialtyId : ''); setCompCopyLevel(competencyFilterLevel || ''); setCompCopyOverwrite(false); setCompCopyModalOpen(true) }} className="flex items-center gap-1.5 px-3 py-1 bg-white text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-50 transition-colors border border-blue-200">
                                  <Copy className="w-3.5 h-3.5" />{txt.copyFromSpecialty}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {group.rows.map((sc) => {
                          const subject = subjects.find(s => s.id === sc.subject_id)
                          const level = levels.find(l => l.id === sc.level_id)
                          const t1 = getCompetencyCount(sc.id, 1)
                          const t2 = getCompetencyCount(sc.id, 2)
                          const t3 = getCompetencyCount(sc.id, 3)
                          return (
                            <tr key={sc.id} className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-3 px-4">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-100 text-emerald-700">{sc.subject_code || subject?.code || '—'}</span>
                                <span className="text-sm text-gray-700 ml-2">{sc.subject_label_fr || subject?.label_fr || '—'}</span>
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-700">{sc.level_name_fr || level?.label_fr || '—'}</td>
                              <td className="py-3 px-4 text-sm text-gray-700">{sc.specialty_name || '—'}</td>
                              <td className="py-3 px-4">
                                {group.subsystem ? (
                                  <span className={cn('inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border', group.subsystem === 'francophone' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200')}>
                                    {group.subsystem === 'francophone' ? txt.francophoneSubsystem : txt.anglophoneSubsystem}
                                  </span>
                                ) : (<span className="text-xs text-gray-400">—</span>)}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', t1 > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-400 border border-gray-200')}>{t1} comp.</span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', t2 > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-400 border border-gray-200')}>{t2} comp.</span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', t3 > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-400 border border-gray-200')}>{t3} comp.</span>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {canManageSubjects && (
                                    <>
                                      <button onClick={() => openCompetencyModal(sc)} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 transition-colors border border-emerald-200">
                                        <Settings2 className="w-3.5 h-3.5" />{txt.configureCompetencies}
                                      </button>
                                      <button onClick={() => openScModal('edit', sc)} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors" title={txt.edit}>
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => openDeleteModal(sc.id, 'subject-class', `${sc.subject_label_fr || subject?.label_fr || 'Matiere'} - ${sc.specialty_name || 'Specialite'}`)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title={txt.delete}>
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
              {competencyFilteredSubjectSpecialties.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium">{txt.noSubjectClasses}</p>
                  <p className="text-sm mt-1">{txt.noCompetencyRows}</p>
                </div>
              )}

            </div>
          </div>
        )}

        {/* ============ TAB: SUBJECTS ============ */}
        {activeTab === 'subjects' && (
          <div className="space-y-6">
            {!canManageSubjects && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700">{txt.readOnly}</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <select value={subjectSortField} onChange={(e) => setSubjectSortField(e.target.value as SortField)} className="pl-3 pr-8 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 appearance-none">
                    <option value="label_fr">{txt.labelFr}</option>
                    <option value="code">{txt.code}</option>
                    <option value="abbreviation">{txt.abbreviation}</option>
                    <option value="status">{txt.status}</option>
                    <option value="section_name">{txt.section}</option>
                    <option value="specialty_name">{txt.specialty}</option>
                    <option value="created_at">{txt.generatedOn}</option>
                  </select>
                  <ArrowUpDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <button onClick={() => setSubjectSortDir(prev => prev === 'asc' ? 'desc' : 'asc')} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors">{subjectSortDir === 'asc' ? '↑' : '↓'}</button>
                <select value={subjectFilterStatus} onChange={(e) => setSubjectFilterStatus(e.target.value)} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400">
                  <option value="">{txt.all}</option>
                  <option value="ACTIVE">{txt.active}</option>
                  <option value="INACTIVE">{txt.inactive}</option>
                </select>
                <select value={subjectFilterSection} onChange={(e) => setSubjectFilterSection(Number(e.target.value) || '')} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400">
                  <option value="">{txt.all} {txt.section.toLowerCase()}</option>
                  {sections.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>))}
                </select>
                <div className="flex items-center gap-1">
                  <select value={subjectFilterGroup} onChange={(e) => setSubjectFilterGroup(e.target.value)} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400">
                    <option value="">{txt.all} {txt.groupName.toLowerCase()}</option>
                    {subjectGroups.map(g => (<option key={g} value={g}>{g}</option>))}
                  </select>
                  <button onClick={openGroupModal} className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors border border-purple-200" title={txt.manageGroups}><Plus className="w-4 h-4" /></button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {canManageSubjects && (
                  <button onClick={() => openSubjectModal('create')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-purple-800 transition-all shadow-md shadow-purple-200"><Plus className="w-4 h-4" />{txt.createSubject}</button>
                )}
                <div className="flex items-center gap-1">
                  <button onClick={() => handleSubjectExport('excel')} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors border border-emerald-200" title="Excel"><FileText className="w-4 h-4" />Excel</button>
                  <button onClick={() => handleSubjectExport('csv')} className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors border border-blue-200" title="CSV"><ArrowDown className="w-4 h-4" />CSV</button>
                  <button onClick={() => handleSubjectExport('pdf')} className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors border border-red-200" title="PDF"><Printer className="w-4 h-4" />PDF</button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><BookMarked className="w-5 h-5 text-purple-600" />{txt.subjectList}</h2>
                <span className="text-sm text-gray-500">{filteredSubjects.length} {txt.results}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-purple-600" onClick={() => handleSubjectSort('code')}>{txt.code} {subjectSortField === 'code' && (subjectSortDir === 'asc' ? '↑' : '↓')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-purple-600" onClick={() => handleSubjectSort('label_fr')}>{txt.labelFr} {subjectSortField === 'label_fr' && (subjectSortDir === 'asc' ? '↑' : '↓')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-purple-600" onClick={() => handleSubjectSort('label_en')}>{txt.labelEn} {subjectSortField === 'label_en' && (subjectSortDir === 'asc' ? '↑' : '↓')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-24 cursor-pointer hover:text-purple-600" onClick={() => handleSubjectSort('abbreviation')}>{txt.abbreviation} {subjectSortField === 'abbreviation' && (subjectSortDir === 'asc' ? '↑' : '↓')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-32 cursor-pointer hover:text-purple-600" onClick={() => handleSubjectSort('group_name')}>{txt.groupName} {subjectSortField === 'group_name' && (subjectSortDir === 'asc' ? '↑' : '↓')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-purple-600" onClick={() => handleSubjectSort('section_name')}>{txt.section} {subjectSortField === 'section_name' && (subjectSortDir === 'asc' ? '↑' : '↓')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-purple-600" onClick={() => handleSubjectSort('specialty_name')}>{txt.specialty} {subjectSortField === 'specialty_name' && (subjectSortDir === 'asc' ? '↑' : '↓')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-24 cursor-pointer hover:text-purple-600" onClick={() => handleSubjectSort('status')}>{txt.status} {subjectSortField === 'status' && (subjectSortDir === 'asc' ? '↑' : '↓')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-32">{lang === 'fr' ? 'AP' : 'AP'}</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-24">{txt.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredSubjects.map((subject) => (
                      <tr key={subject.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4"><span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-purple-100 text-purple-700">{subject.code}</span></td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{subject.label_fr}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{subject.label_en}</td>
                        <td className="py-3 px-4"><span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-gray-100 text-gray-700">{subject.abbreviation}</span></td>
                        <td className="py-3 px-4">{subject.group_name ? (<span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">{subject.group_name}</span>) : (<span className="text-xs text-gray-400">—</span>)}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{subject.section_name || sections.find(s => s.id === subject.section_id)?.label_fr || '—'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{subject.specialty_name || specialties.find(s => s.id === subject.specialty_id)?.label_fr || '—'}</td>
                        <td className="py-3 px-4">
                          <button onClick={() => canManageSubjects && handleToggleSubjectStatus(subject)} disabled={!canManageSubjects} className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all', subject.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-700 border border-gray-200', !canManageSubjects && 'opacity-60 cursor-not-allowed')}>
                            {subject.status === 'ACTIVE' ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                            {subject.status === 'ACTIVE' ? txt.openStatus : txt.closedStatus}
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            {subject.teacher_name ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" title={lang === 'fr' ? 'Animateur Pedagogique' : 'Pedagogical Animator'}>
                                <UserCheck className="w-3 h-3" />{subject.teacher_name}
                              </span>
                            ) : (<span className="text-xs text-gray-400 italic">{lang === 'fr' ? 'Sans AP' : 'No AP'}</span>)}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canManageSubjects ? (
                              <>
                                <button onClick={() => openApModal(subject)} className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors" title={lang === 'fr' ? 'Assigner AP' : 'Assign AP'}><UserCheck className="w-4 h-4" /></button>
                                <button onClick={() => openSubjectModal('edit', subject)} className="p-1.5 rounded-lg text-purple-500 hover:bg-purple-50 transition-colors" title={txt.edit}><Edit2 className="w-4 h-4" /></button>
                                <button onClick={() => openDeleteModal(subject.id, 'subject', subject.label_fr)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title={txt.delete}><Trash2 className="w-4 h-4" /></button>
                              </>
                            ) : (<Eye className="w-4 h-4 text-gray-400" />)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredSubjects.length === 0 && (
                <div className="text-center py-12 text-gray-400"><BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="text-lg font-medium">{txt.noSubjects}</p><p className="text-sm mt-1">{txt.createSubjectStart}</p></div>
              )}
            </div>
          </div>
        )}

        {/* ============ TAB: SUBJECT-CLASSES ============ */}
        {activeTab === 'subject-classes' && (
          <div className="space-y-6">
            {!canManageSubjects && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700">{txt.readOnly}</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <select value={scFilterSpecialty} onChange={(e) => setScFilterSpecialty(Number(e.target.value) || '')} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400">
                  <option value="">{txt.all} {txt.classFilter.toLowerCase()}</option>
                  {specialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}
                </select>
                <select value={scFilterSubject} onChange={(e) => setScFilterSubject(Number(e.target.value) || '')} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400">
                  <option value="">{txt.all} {txt.subjectFilter.toLowerCase()}</option>
                  {subjects.map(s => (<option key={s.id} value={s.id}>{s.label_fr} ({s.code})</option>))}
                </select>
                <select value={scFilterLevel} onChange={(e) => setScFilterLevel(Number(e.target.value) || '')} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400">
                  <option value="">{txt.all} {txt.level.toLowerCase()}</option>
                  {levels.map(l => (<option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                {canManageSubjects && (
                  <>
                    <button onClick={() => setDuplicateModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-md shadow-blue-200"><Copy className="w-4 h-4" />{lang === 'fr' ? 'Dupliquer' : 'Duplicate'}</button>
                    <button onClick={() => openScModal('bulk-cycle')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg text-sm font-medium hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-md shadow-emerald-200"><CheckCircle2 className="w-4 h-4" />{txt.assignByLevel}</button>
                    <button onClick={() => openScModal('create')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg text-sm font-medium hover:from-indigo-700 hover:to-indigo-800 transition-all shadow-md shadow-indigo-200"><BookPlus className="w-4 h-4" />{txt.assignSubject}</button>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><BookPlus className="w-5 h-5 text-indigo-600" />{txt.subjectClassList}</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{filteredSubjectSpecialties.length} {txt.results}</span>
                  <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => handleSubjectClassExport('excel')} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors border border-emerald-200" title="Excel"><FileText className="w-4 h-4" />Excel</button>
                    <button onClick={() => handleSubjectClassExport('csv')} className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors border border-blue-200" title="CSV"><ArrowDown className="w-4 h-4" />CSV</button>
                    <button onClick={() => handleSubjectClassExport('pdf')} className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors border border-red-200" title="PDF"><Printer className="w-4 h-4" />PDF</button>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{txt.classFilter}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{txt.level}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{txt.subjectFilter}</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-24">{txt.coefficient}</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-24">{txt.hoursPerWeek}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-24">{txt.status}</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase w-32">{txt.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredSubjectSpecialties.map((sc) => {
                      const subject = subjects.find(s => s.id === sc.subject_id)
                      const specialty = specialties.find(s => s.id === sc.specialty_id)
                      const level = levels.find(l => l.id === sc.level_id)
                      return (
                        <tr key={sc.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-gray-900">{sc.specialty_name || specialty?.label_fr || '—'}</span>
                            <span className="text-xs text-gray-500 ml-1">({sc.specialty_code || specialty?.code || ''})</span>
                          </td>
                          <td className="py-3 px-4"><span className="text-sm text-gray-700">{sc.level_name_fr || level?.label_fr || '—'}</span><span className="text-xs text-gray-500 ml-1">({sc.level_code || level?.code || ''})</span></td>
                          <td className="py-3 px-4"><span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-indigo-100 text-indigo-700">{sc.subject_code || subject?.code || '—'}</span><span className="text-sm text-gray-700 ml-2">{sc.subject_label_fr || subject?.label_fr || ''}</span></td>
                          <td className="py-3 px-4 text-center"><span className="text-sm font-bold text-gray-700">{sc.coefficient}</span></td>
                          <td className="py-3 px-4 text-center"><span className="text-sm text-gray-600">{sc.hours_per_week}h</span></td>
                          <td className="py-3 px-4"><span className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium', sc.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-700 border border-gray-200')}><Unlock className="w-3 h-3" />{sc.status === 'ACTIVE' ? txt.openStatus : txt.closedStatus}</span></td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canManageSubjects ? (
                                <>
                                  <button onClick={() => openCompetencyModal(sc)} className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50 transition-colors" title={txt.configureCompetencies}><Sparkles className="w-4 h-4" /></button>
                                  <button onClick={() => openScModal('edit', sc)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 transition-colors" title={txt.edit}><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={() => openDeleteModal(sc.id, 'subject-class', `${sc.subject_label_fr || subject?.label_fr || 'Matiere'} - ${sc.specialty_name || specialty?.label_fr || 'Specialite'}`)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title={txt.delete}><Trash2 className="w-4 h-4" /></button>
                                </>
                              ) : (<Eye className="w-4 h-4 text-gray-400" />)}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {filteredSubjectSpecialties.length === 0 && (
                <div className="text-center py-12 text-gray-400"><BookPlus className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="text-lg font-medium">{txt.noSubjectClasses}</p><p className="text-sm mt-1">{txt.createSubjectClassStart}</p></div>
              )}
            </div>
          </div>
        )}

        {/* ============ TAB: CLASSES ============ */}
        {activeTab === 'classes' && (
          <>
            {(!hasCycles || !hasLevels) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div><p className="text-sm font-medium text-amber-800">{txt.incompleteStructure}</p><p className="text-xs text-amber-700 mt-1">{!hasCycles ? txt.createCyclesFirst : txt.createLevelsFirst}</p></div>
              </div>
            )}

            {showFilters && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-5 bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-100">
                  <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Filter className="w-5 h-5 text-blue-600" />{txt.filterAndCreate}</h2>
                </div>
                <div className="p-5 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.section} *</label>
                      <div className="relative">
                        <School className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <select value={selectedSection} onChange={(e) => { setSelectedSection(Number(e.target.value) || ''); setSelectedCycle(''); setSelectedSpecialty(''); setSelectedLevel('') }} className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none">
                          <option value="">{txt.select}</option>
                          {sections.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.cycle} *</label>
                      <div className="relative">
                        <RotateCcw className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <select value={selectedCycle} onChange={(e) => { setSelectedCycle(Number(e.target.value) || ''); setSelectedSpecialty(''); setSelectedLevel('') }} disabled={!selectedSection || cycles.length === 0} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none', !selectedSection || cycles.length === 0 ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')}>
                          <option value="">{txt.select}</option>
                          {filteredCycles.map(c => (<option key={c.id} value={c.id}>{lang === 'fr' ? c.label_fr : c.label_en}</option>))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                      {selectedSection && cycles.length === 0 && (<p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><Info className="w-3 h-3" />{txt.noCyclesForSection}</p>)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.specialty} *</label>
                      <div className="relative">
                        <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <select value={selectedSpecialty} onChange={(e) => { setSelectedSpecialty(Number(e.target.value) || ''); setSelectedLevel('') }} disabled={!selectedCycle} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none', !selectedCycle ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')}>
                          <option value="">{txt.select}</option>
                          {filteredSpecialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                      {selectedCycle && filteredSpecialties.length === 0 && (<p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{txt.noSpecialty}</p>)}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.level} *</label>
                      <div className="relative">
                        <ArrowUp className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <select value={selectedLevel} onChange={(e) => setSelectedLevel(Number(e.target.value) || '')} disabled={!selectedSpecialty || filteredLevels.length === 0} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none', !selectedSpecialty || filteredLevels.length === 0 ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')}>
                          <option value="">{txt.select}</option>
                          {filteredLevels.map(l => (<option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                      {selectedCycle && filteredLevels.length === 0 && (<p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><Info className="w-3 h-3" />{txt.noLevelsForCycle}</p>)}
                    </div>
                    <div className="flex items-end">
                      <button onClick={handleCreateClass} disabled={!selectedLevel || !className.trim() || !classAbbrev.trim()} className={cn('flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium transition-all shadow-md w-full', selectedLevel && className.trim() && classAbbrev.trim() ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-blue-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed')}><Plus className="w-4 h-4" />{txt.addClass}</button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.className} *</label>
                      <input type="text" value={className} onChange={(e) => setClassName(e.target.value)} disabled={!selectedLevel} placeholder={lang === 'fr' ? 'Ex: Quatrieme Allemande' : 'Ex: Fourth German'} className={cn('w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400', !selectedLevel ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.classAbbrev} *</label>
                      <input type="text" value={classAbbrev} onChange={(e) => setClassAbbrev(e.target.value.toUpperCase())} disabled={!selectedLevel} placeholder={lang === 'fr' ? 'Ex: 4e ALL' : 'Ex: 4th GER'} maxLength={20} className={cn('w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400', !selectedLevel ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')} />
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                    <div className="pt-0.5">
                      <input type="checkbox" id="permanent-class" checked={isPermanentClass} onChange={(e) => setIsPermanentClass(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                    </div>
                    <div>
                      <label htmlFor="permanent-class" className="text-sm font-medium text-gray-800 cursor-pointer">{txt.permanentClass}</label>
                      <p className="text-xs text-gray-500 mt-0.5">{txt.permanentClassHelp}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" ref={tableRef}>
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Building2 className="w-5 h-5 text-blue-600" />{txt.classList}</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{filteredClasses.length} {txt.results}</span>
                  <div className="flex items-center gap-1 ml-2">
                    <button onClick={() => handleExport('excel')} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors border border-emerald-200" title="Excel"><FileText className="w-4 h-4" />Excel</button>
                    <button onClick={() => handleExport('csv')} className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors border border-blue-200" title="CSV"><ArrowDown className="w-4 h-4" />CSV</button>
                    <button onClick={() => handleExport('pdf')} className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors border border-red-200" title="PDF"><Printer className="w-4 h-4" />PDF</button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.section2}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.cycle2}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.specialty2}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">{txt.level2}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.class2}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">{txt.abbreviation}</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">{txt.status}</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">{txt.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredClasses.map((cls) => {
                      const isEditing = editingId === cls.id
                      const isOpen = cls.status === 'open'
                      const display = getClassDisplayData(cls)
                      return (
                        <tr key={cls.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="py-3 px-4"><span className={cn('inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border', display.sectionId === 1 ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-amber-50 text-amber-700 border-amber-100')}>{display.sectionName}</span></td>
                          <td className="py-3 px-4"><span className="text-sm text-gray-700">{display.cycleName}</span></td>
                          <td className="py-3 px-4">
                            {isEditing ? (
                              <select value={editSpecialty} onChange={(e) => setEditSpecialty(Number(e.target.value) || '')} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white">
                                <option value="">{txt.select}</option>
                                {specialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}
                              </select>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">{display.specialtyName}</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {isEditing ? (
                              <select value={editLevel} onChange={(e) => setEditLevel(Number(e.target.value) || '')} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white">
                                <option value="">{txt.select}</option>
                                {levels.map(l => (<option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>))}
                              </select>
                            ) : (
                              <span className="text-sm font-medium text-gray-700">{display.levelName}</span>
                            )}
                          </td>
                          <td className="py-3 px-4">{isEditing ? (<input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/30" autoFocus />) : (<span className="text-sm font-medium text-gray-900">{cls.name}</span>)}</td>
                          <td className="py-3 px-4">{isEditing ? (<input type="text" value={editAbbrev} onChange={(e) => setEditAbbrev(e.target.value.toUpperCase())} className="w-20 px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/30" maxLength={20} />) : (<span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold tracking-wide bg-gray-100 text-gray-700">{cls.abbreviation}</span>)}</td>
                          <td className="py-3 px-4 text-center">
                            <button onClick={() => handleToggleStatus(cls)} className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all', isOpen ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100' : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100')}><Unlock className="w-3 h-3" />{isOpen ? txt.openStatus : txt.closedStatus}</button>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {isEditing ? (
                                <><button onClick={() => saveEdit(cls.id)} className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50 transition-colors"><Check className="w-4 h-4" /></button><button onClick={cancelEdit} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 transition-colors"><X className="w-4 h-4" /></button></>
                              ) : (
                                <><button onClick={() => startEdit(cls)} className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors" title={txt.edit}><Edit2 className="w-4 h-4" /></button><button onClick={() => openDeleteModal(cls.id, 'class', cls.name)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title={txt.delete}><Trash2 className="w-4 h-4" /></button></>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {filteredClasses.length === 0 && (
                <div className="text-center py-12 text-gray-400"><School className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="text-lg font-medium">{txt.noClasses}</p><p className="text-sm mt-1">{txt.useFilters}</p></div>
              )}
            </div>
          </>
        )}

        {/* ============ TAB: CYCLES ============ */}
        {activeTab === 'cycles' && (
          <div className="space-y-6">
            {!hasSections && (<div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3"><AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" /><div><p className="text-sm font-medium text-amber-800">{txt.noSections}</p><p className="text-xs text-amber-700 mt-1">{txt.configureSections}</p></div></div>)}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 bg-gradient-to-r from-blue-50 to-blue-100/50 border-b border-blue-100"><h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><RotateCcw className="w-5 h-5 text-blue-600" />{editingCycleId ? txt.editCycle : txt.createCycle}</h2></div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.section} *</label><select value={selectedSection} onChange={(e) => setSelectedSection(Number(e.target.value) || '')} disabled={!hasSections} className={cn('w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400', !hasSections ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')}><option value="">{txt.select}</option>{sections.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>))}</select></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.code} *</label><input type="text" value={cycleForm.code} onChange={(e) => setCycleForm({ ...cycleForm, code: e.target.value.toUpperCase() })} placeholder="1ST, 2ND" className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.labelFr} *</label><input type="text" value={cycleForm.label_fr} onChange={(e) => setCycleForm({ ...cycleForm, label_fr: e.target.value })} placeholder={lang === 'fr' ? '1er Cycle' : '1st Cycle'} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.labelEn}</label><input type="text" value={cycleForm.label_en} onChange={(e) => setCycleForm({ ...cycleForm, label_en: e.target.value })} placeholder={lang === 'fr' ? '1st Cycle' : '1st Cycle'} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                </div>
                <div className="flex items-center gap-3">
                  {editingCycleId ? (
                    <><button onClick={() => handleUpdateCycle(editingCycleId)} className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-md shadow-blue-200"><Save className="w-4 h-4" />{txt.save}</button><button onClick={() => { setEditingCycleId(null); setCycleForm({ code: '', label_fr: '', label_en: '', order: 1 }) }} className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all"><X className="w-4 h-4" />{txt.cancel}</button></>
                  ) : (
                    <button onClick={handleCreateCycle} disabled={!selectedSection || !cycleForm.code.trim() || !cycleForm.label_fr.trim()} className={cn('flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium transition-all shadow-md', selectedSection && cycleForm.code.trim() && cycleForm.label_fr.trim() ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-blue-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed')}><Plus className="w-4 h-4" />{txt.addCycle}</button>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100"><h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><RotateCcw className="w-5 h-5 text-blue-600" />{txt.cycleList}</h2></div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b-2 border-gray-100"><th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.section}</th><th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.code}</th><th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.labelFr}</th><th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.labelEn}</th><th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">{txt.actions}</th></tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {cycles.filter(cycle => !userSchoolId || (cycle.section_id ? sections.find(s => s.id === cycle.section_id)?.school_id === userSchoolId : false)).map((cycle) => (
                      <tr key={cycle.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4"><span className="text-sm text-gray-700">{sections.find(s => s.id === cycle.section_id)?.label_fr || '—'}</span></td>
                        <td className="py-3 px-4"><span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold tracking-wide bg-blue-100 text-blue-700">{cycle.code}</span></td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{cycle.label_fr}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{cycle.label_en}</td>
                        <td className="py-3 px-4 text-right"><div className="flex items-center justify-end gap-1"><button onClick={() => { setEditingCycleId(cycle.id); setCycleForm({ code: cycle.code, label_fr: cycle.label_fr, label_en: cycle.label_en, order: cycle.order }); setSelectedSection(cycle.section_id) }} className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"><Edit2 className="w-4 h-4" /></button><button onClick={() => openDeleteModal(cycle.id, 'cycle', cycle.label_fr)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {cycles.length === 0 && (<div className="text-center py-12 text-gray-400"><RotateCcw className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="text-lg font-medium">{txt.noCycles2}</p><p className="text-sm mt-1">{txt.createCycleStart}</p></div>)}
            </div>
          </div>
        )}

        {/* ============ TAB: LEVELS ============ */}
        {activeTab === 'levels' && (
          <div className="space-y-6">
            {!hasCycles && (<div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3"><AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" /><div><p className="text-sm font-medium text-amber-800">{txt.noCycles}</p><p className="text-xs text-amber-700 mt-1">{txt.createCyclesFirst2}</p></div></div>)}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-amber-100"><h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><ArrowUp className="w-5 h-5 text-amber-600" />{editingLevelId ? txt.editLevel : txt.createLevel}</h2></div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.cycle} *</label><select value={selectedCycle} onChange={(e) => setSelectedCycle(Number(e.target.value) || '')} disabled={cycles.length === 0} className={cn('w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400', cycles.length === 0 ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200')}><option value="">{txt.select}</option>{cycles.map(c => (<option key={c.id} value={c.id}>{lang === 'fr' ? c.label_fr : c.label_en} — {sections.find(s => s.id === c.section_id)?.label_fr || ''}</option>))}</select></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.code} *</label><input type="text" value={levelForm.code} onChange={(e) => setLevelForm({ ...levelForm, code: e.target.value.toLowerCase() })} placeholder="6e, 5e, 2nd" className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.labelFr} *</label><input type="text" value={levelForm.label_fr} onChange={(e) => setLevelForm({ ...levelForm, label_fr: e.target.value })} placeholder={lang === 'fr' ? 'Sixieme' : 'Sixth'} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.labelEn}</label><input type="text" value={levelForm.label_en} onChange={(e) => setLevelForm({ ...levelForm, label_en: e.target.value })} placeholder={lang === 'fr' ? 'Sixth' : 'Sixth'} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" /></div>
                </div>
                <div className="flex items-center gap-3">
                  {editingLevelId ? (
                    <><button onClick={() => handleUpdateLevel(editingLevelId)} className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-xl text-sm font-medium hover:from-amber-700 hover:to-amber-800 transition-all shadow-md shadow-amber-200"><Save className="w-4 h-4" />{txt.save}</button><button onClick={() => { setEditingLevelId(null); setLevelForm({ code: '', label_fr: '', label_en: '', order: 1 }) }} className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all"><X className="w-4 h-4" />{txt.cancel}</button></>
                  ) : (
                    <button onClick={handleCreateLevel} disabled={!selectedCycle || !levelForm.code.trim() || !levelForm.label_fr.trim()} className={cn('flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium transition-all shadow-md', selectedCycle && levelForm.code.trim() && levelForm.label_fr.trim() ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white hover:from-amber-700 hover:to-amber-800 shadow-amber-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed')}><Plus className="w-4 h-4" />{txt.addLevel}</button>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100"><h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><ArrowUp className="w-5 h-5 text-amber-600" />{txt.levelList}</h2></div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b-2 border-gray-100"><th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.cycle}</th><th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.code}</th><th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.labelFr}</th><th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.labelEn}</th><th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">{txt.actions}</th></tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {levels.filter(level => {
                      if (!userSchoolId) return true
                      const cycle = cycles.find(c => c.id === level.cycle_id)
                      if (!cycle) return false
                      const sec = sections.find(s => s.id === cycle.section_id)
                      return sec ? (sec.school_id || 1) === userSchoolId : false
                    }).map((level) => (
                      <tr key={level.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4"><span className="text-sm text-gray-700">{cycles.find(c => c.id === level.cycle_id)?.label_fr || '—'}</span></td>
                        <td className="py-3 px-4"><span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold tracking-wide bg-amber-100 text-amber-700">{level.code}</span></td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{level.label_fr}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{level.label_en}</td>
                        <td className="py-3 px-4 text-right"><div className="flex items-center justify-end gap-1"><button onClick={() => { setEditingLevelId(level.id); setLevelForm({ code: level.code, label_fr: level.label_fr, label_en: level.label_en, order: level.order }); setSelectedCycle(level.cycle_id) }} className="p-1.5 rounded-lg text-amber-500 hover:bg-amber-50 transition-colors"><Edit2 className="w-4 h-4" /></button><button onClick={() => openDeleteModal(level.id, 'level', level.label_fr)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {levels.length === 0 && (<div className="text-center py-12 text-gray-400"><ArrowUp className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="text-lg font-medium">{txt.noLevels}</p><p className="text-sm mt-1">{txt.createLevelStart}</p></div>)}
            </div>
          </div>
        )}
      </div>

      {/* ==================== MODALS ==================== */}

      {/* SUBJECT MODAL */}
      {subjectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeSubjectModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><BookOpen className="w-5 h-5 text-purple-600" />{subjectModalMode === 'create' ? txt.modalCreateSubject : txt.modalEditSubject}</h3>
              <button onClick={closeSubjectModal} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.code} *</label><input type="text" value={subjectForm.code} onChange={(e) => setSubjectForm({ ...subjectForm, code: e.target.value.toUpperCase() })} placeholder="MATH, FRAN" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.labelFr} *</label><input type="text" value={subjectForm.label_fr} onChange={(e) => setSubjectForm({ ...subjectForm, label_fr: e.target.value })} placeholder="Mathematiques" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.labelEn}</label><input type="text" value={subjectForm.label_en} onChange={(e) => setSubjectForm({ ...subjectForm, label_en: e.target.value })} placeholder="Mathematics" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.abbreviation} *</label><input type="text" value={subjectForm.abbreviation} onChange={(e) => setSubjectForm({ ...subjectForm, abbreviation: e.target.value.toUpperCase() })} placeholder="MATH" maxLength={10} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.groupName}</label><div className="flex items-center gap-2"><select value={subjectForm.group_name} onChange={(e) => setSubjectForm({ ...subjectForm, group_name: e.target.value })} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"><option value="">{txt.select}</option>{subjectGroups.map(g => (<option key={g} value={g}>{g}</option>))}</select><button onClick={openGroupModal} className="px-3 py-2.5 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-100 transition-colors border border-purple-200 text-sm font-medium" type="button">{txt.manageGroups}</button></div></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.section}</label><select value={subjectForm.section_id} onChange={(e) => setSubjectForm({ ...subjectForm, section_id: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"><option value="">{txt.select}</option>{sections.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>))}</select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.specialty}</label><select value={subjectForm.specialty_id} onChange={(e) => setSubjectForm({ ...subjectForm, specialty_id: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"><option value="">{txt.select}</option>{specialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}</select></div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button onClick={closeSubjectModal} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">{txt.cancel}</button>
              <button onClick={handleSaveSubject} disabled={isSubmitting || !subjectForm.code.trim() || !subjectForm.label_fr.trim() || !subjectForm.abbreviation.trim()} className={cn('flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl', isSubmitting || !subjectForm.code.trim() || !subjectForm.label_fr.trim() || !subjectForm.abbreviation.trim() ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800')}>{isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{txt.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* SUBJECT-SPECIALTY MODAL */}
      {scModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeScModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><BookPlus className="w-5 h-5 text-indigo-600" />{scModalMode === 'bulk-cycle' ? txt.assignByLevel : scModalMode === 'create' ? txt.assignSubject : txt.edit}</h3>
              <button onClick={closeScModal} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {scModalMode === 'bulk-cycle' ? (
                <>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><p className="text-xs text-emerald-700">{txt.assignToAllClasses}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.subsystem} *</label><select value={scForm.subsystem} onChange={(e) => setScForm({ ...scForm, subsystem: e.target.value as any })} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"><option value="">{txt.selectSubsystem}</option><option value="francophone">{txt.francophoneSubsystem} — {txt.generalSecondary}</option><option value="francophone-bilingual">{txt.francophoneSubsystem} — {txt.bilingualProgram}</option><option value="anglophone">{txt.anglophoneSubsystem} — {txt.generalSecondary}</option><option value="anglophone-bilingual">{txt.anglophoneSubsystem} — {txt.bilingualProgram}</option></select><p className="text-xs text-gray-500 mt-1">{txt.subsystemHelp}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.cycle} *</label><select value={scForm.cycle_id} onChange={(e) => setScForm({ ...scForm, cycle_id: e.target.value })} className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"><option value="">{txt.select}</option>{cycles.filter(c => { if (!scForm.subsystem) return true; const section = sections.find(s => s.id === c.section_id); if (!section) return false; const sectionSub = getSectionSubsystem(section); if (scForm.subsystem === 'francophone' || scForm.subsystem === 'francophone-bilingual') return sectionSub === 'francophone'; if (scForm.subsystem === 'anglophone' || scForm.subsystem === 'anglophone-bilingual') return sectionSub === 'anglophone'; return true }).map(c => (<option key={c.id} value={c.id}>{lang === 'fr' ? c.label_fr : c.label_en}</option>))}</select></div>
                  {scForm.cycle_id && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                      <p className="text-xs font-medium text-blue-700 mb-1.5">{lang === 'fr' ? 'Niveaux qui seront attribues :' : 'Levels to be assigned :'}</p>
                      <div className="flex flex-wrap gap-1.5">{levels.filter(l => l.cycle_id === Number(scForm.cycle_id)).map(l => (<span key={l.id} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">{lang === 'fr' ? l.label_fr : l.label_en}</span>))}</div>
                      <p className="text-xs text-blue-600 mt-1.5">{lang === 'fr' ? `${levels.filter(l => l.cycle_id === Number(scForm.cycle_id)).length} enregistrement(s) seront crees` : `${levels.filter(l => l.cycle_id === Number(scForm.cycle_id)).length} record(s) will be created`}</p>
                    </div>
                  )}
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.specialty} *</label><select value={scForm.specialty_id} onChange={(e) => setScForm({ ...scForm, specialty_id: e.target.value })} className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"><option value="">{txt.select}</option>{specialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}</select></div>
                </>
              ) : (
                <>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.level} *</label><select value={scForm.level_id} onChange={(e) => setScForm({ ...scForm, level_id: e.target.value })} className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"><option value="">{txt.select}</option>{levels.map(l => (<option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>))}</select></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.classFilter} *</label><select value={scForm.specialty_id} onChange={(e) => setScForm({ ...scForm, specialty_id: e.target.value })} className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"><option value="">{txt.select}</option>{specialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}</select></div>
                </>
              )}
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.subjectFilter} *</label><select value={scForm.subject_id} onChange={(e) => setScForm({ ...scForm, subject_id: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"><option value="">{txt.select}</option>{subjects.map(s => (<option key={s.id} value={s.id}>{s.label_fr} ({s.code})</option>))}</select></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.coefficient}</label><input type="number" step="0.5" min="0.5" value={scForm.coefficient} onChange={(e) => setScForm({ ...scForm, coefficient: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.hoursPerWeek}</label><input type="number" step="0.5" min="0" value={scForm.hours_per_week} onChange={(e) => setScForm({ ...scForm, hours_per_week: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" /></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.status}</label><select value={scForm.status} onChange={(e) => setScForm({ ...scForm, status: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"><option value="ACTIVE">{txt.active}</option><option value="INACTIVE">{txt.inactive}</option></select></div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button onClick={closeScModal} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">{txt.cancel}</button>
              <button onClick={handleSaveSubjectSpecialty} disabled={isSubmitting || !scForm.subject_id || (scModalMode === 'bulk-cycle' ? (!scForm.cycle_id || !scForm.specialty_id) : !scForm.specialty_id)} className={cn('flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl', isSubmitting || !scForm.subject_id || (scModalMode === 'bulk-cycle' ? !scForm.cycle_id : !scForm.specialty_id) ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800')}>{isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{txt.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* COMPETENCY MODAL */}
      {competencyModalOpen && competencyModalSubjectSpecialty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeCompetencyModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                {txt.subjectCompetencyTitle} — {competencyModalSubjectSpecialty.subject_label_fr || subjects.find(s => s.id === competencyModalSubjectSpecialty?.subject_id)?.label_fr || ''}
                <span className="text-sm font-normal text-gray-500">({competencyModalSubjectSpecialty.specialty_name} — {competencyModalSubjectSpecialty.level_name_fr})</span>
              </h3>
              <button onClick={closeCompetencyModal} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-6">
              {/* Copie depuis une autre specialite (meme matiere) */}
              {canManageSubjects && (() => {
                const otherRows = subjectSpecialties.filter(sc =>
                  sc.subject_id === competencyModalSubjectSpecialty.subject_id &&
                  sc.id !== competencyModalSubjectSpecialty.id &&
                  (!userSchoolId || sc.school_id === userSchoolId || sc.school_id === null || sc.school_id === undefined)
                )
                if (otherRows.length === 0) return null
                return (
                  <div className="bg-blue-50/60 rounded-xl p-4 border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2"><Copy className="w-4 h-4" />{txt.copySubjectCompetencies}</h4>
                    <div className="flex items-center gap-2">
                      <select value={modalCopySource} onChange={(e) => setModalCopySource(Number(e.target.value) || '')} className="flex-1 px-3 py-2 bg-white border border-blue-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                        <option value="">{txt.select}</option>
                        {otherRows.map(sc => (
                          <option key={sc.id} value={sc.id}>
                            {sc.specialty_name || specialties.find(s => s.id === sc.specialty_id)?.label_fr || '?'} — {sc.level_name_fr || levels.find(l => l.id === sc.level_id)?.label_fr || '?'}
                          </option>
                        ))}
                      </select>
                      <button onClick={handleModalCopy} disabled={isCopying || !modalCopySource} className={cn('flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all', !modalCopySource || isCopying ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800')}>
                        {isCopying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                        {txt.copy}
                      </button>
                    </div>
                  </div>
                )
              })()}

              {/* ✅ Zone danger : supprimer TOUTES les compétences de CETTE matière */}
              {canManageSubjects && (
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />{lang === 'fr' ? 'Zone danger' : 'Danger zone'}
                  </h4>
                  {!confirmDeleteAllSubject ? (
                    <button onClick={() => setConfirmDeleteAllSubject(true)} className="flex items-center gap-2 px-4 py-2 bg-white text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors border border-red-300">
                      <Trash2 className="w-4 h-4" />
                      {lang === 'fr' ? `Supprimer toutes les compétences de « ${competencyModalSubjectSpecialty.subject_label_fr} » (${competencyModalSubjectSpecialty.specialty_name})` : `Delete all competencies of "${competencyModalSubjectSpecialty.subject_label_fr}" (${competencyModalSubjectSpecialty.specialty_name})`}
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-red-700 flex-1 min-w-[200px]">
                        {lang === 'fr' ? `Confirmer la suppression définitive des ${competencies.filter(cc => cc.subject_specialty_id === competencyModalSubjectSpecialty?.id).length} compétence(s) de cette matière ?` : 'Permanently delete all competencies of this subject?'}
                      </span>
                      <button onClick={handleDeleteAllSubjectCompetencies} disabled={deletingAllSubject} className={cn('flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all', deletingAllSubject ? 'bg-red-300 cursor-not-allowed' : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800')}>
                        {deletingAllSubject ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        {lang === 'fr' ? 'Confirmer' : 'Confirm'}
                      </button>
                      <button onClick={() => setConfirmDeleteAllSubject(false)} className="px-4 py-2 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors border border-gray-300">
                        {txt.cancel}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Competency Form */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">{editingCompetencyId ? txt.edit : txt.addCompetency}</h4>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="md:col-span-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Trimestre</label>
                    <select value={competencyForm.trimester} onChange={(e) => setCompetencyForm({ ...competencyForm, trimester: Number(e.target.value) })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400">
                      <option value={1}>T1</option>
                      <option value={2}>T2</option>
                      <option value={3}>T3</option>
                    </select>
                  </div>
                  <div className="md:col-span-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">N°</label>
                    <select value={competencyForm.number} onChange={(e) => setCompetencyForm({ ...competencyForm, number: Number(e.target.value) })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400">
                      {[1, 2, 3, 4, 5, 6].map(n => (<option key={n} value={n}>{n}</option>))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{txt.labelFr} *</label>
                    <input type="text" value={competencyForm.label_fr} onChange={(e) => setCompetencyForm({ ...competencyForm, label_fr: e.target.value })} placeholder={lang === 'fr' ? 'Ex: Comprehension orale' : 'Ex: Oral comprehension'} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <button onClick={handleSaveCompetency} disabled={isSubmitting || !competencyForm.label_fr.trim()} className={cn('w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all', !competencyForm.label_fr.trim() ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800 shadow-md shadow-emerald-200')}>
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      {editingCompetencyId ? txt.save : txt.addCompetency}
                    </button>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{txt.labelEn}</label>
                    <input type="text" value={competencyForm.label_en} onChange={(e) => setCompetencyForm({ ...competencyForm, label_en: e.target.value })} placeholder="English label" className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{txt.abbreviation}</label>
                    <input type="text" value={competencyForm.abbreviation} onChange={(e) => setCompetencyForm({ ...competencyForm, abbreviation: e.target.value.toUpperCase() })} placeholder={lang === 'fr' ? 'Ex: CO, CE, EO' : 'Ex: OC, RD, OW'} maxLength={10} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{txt.competencyDesc}</label>
                  <textarea value={competencyForm.description} onChange={(e) => setCompetencyForm({ ...competencyForm, description: e.target.value })} rows={2} placeholder={lang === 'fr' ? 'Description de la competence...' : 'Competency description...'} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 resize-none" />
                </div>
              </div>

              {/* Trimester Columns */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((trimester) => {
                  const trimesterComps = competencies.filter(c => c.subject_specialty_id === competencyModalSubjectSpecialty?.id && c.trimester === trimester)
                  const trimesterLabels = ['', txt.trimester1, txt.trimester2, txt.trimester3]
                  return (
                    <div key={trimester} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-b border-emerald-100 flex items-center justify-between">
                        <h4 className="text-sm font-bold text-emerald-800">{trimesterLabels[trimester]}</h4>
                        <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">{trimesterComps.length}/6</span>
                      </div>
                      <div className="p-3 space-y-2">
                        {trimesterComps.length === 0 && (
                          <p className="text-xs text-gray-400 text-center py-4">{txt.noCompetencies}</p>
                        )}
                        {trimesterComps.map((comp) => (
                          <div key={comp.id} className={cn('p-3 rounded-lg border transition-all', editingCompetencyId === comp.id ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-100 hover:border-gray-200')}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 truncate">
                                  {comp.abbreviation && (
                                    <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 mr-1.5 rounded bg-emerald-600 text-white text-[10px] font-bold align-middle">{comp.abbreviation}</span>
                                  )}
                                  {comp.label_fr}
                                </p>
                                {comp.label_en && <p className="text-xs text-gray-500 truncate">{comp.label_en}</p>}
                                {comp.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{comp.description}</p>}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => startEditCompetency(comp)} className="p-1 rounded-md text-emerald-600 hover:bg-emerald-100 transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleDeleteCompetency(comp.id)} className="p-1 rounded-md text-red-400 hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {trimesterComps.length >= 6 && (
                          <p className="text-xs text-amber-600 text-center py-1">{txt.maxCompetencies}</p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* COMPETENCY COPY MODAL (specialite -> specialite) */}
      {compCopyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setCompCopyModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Copy className="w-5 h-5 text-blue-600" />{txt.copyCompetencies}</h3>
              <button onClick={() => setCompCopyModalOpen(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">{txt.copyHint}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.sourceSpecialty} *</label>
                <select value={compCopySource} onChange={(e) => setCompCopySource(Number(e.target.value) || '')} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                  <option value="">{txt.select}</option>
                  {specialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.targetSpecialty} *</label>
                <select value={compCopyTarget} onChange={(e) => setCompCopyTarget(Number(e.target.value) || '')} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                  <option value="">{txt.select}</option>
                  {specialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{txt.copyLevel}</label>
                <select value={compCopyLevel} onChange={(e) => setCompCopyLevel(Number(e.target.value) || '')} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                  <option value="">{txt.allLevels}</option>
                  {levels.map(l => (<option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="copy-overwrite" checked={compCopyOverwrite} onChange={(e) => setCompCopyOverwrite(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                <label htmlFor="copy-overwrite" className="text-sm text-gray-700">{txt.overwriteExisting}</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button onClick={() => setCompCopyModalOpen(false)} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">{txt.cancel}</button>
              <button onClick={handleGlobalCopy} disabled={isCopying || !compCopySource || !compCopyTarget} className={cn('flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl', isCopying || !compCopySource || !compCopyTarget ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800')}>
                {isCopying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                {txt.copy}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE ALL COMPETENCIES CONFIRM MODAL */}
      {confirmDeleteAllGlobal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDeleteAllGlobal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-red-50 to-red-100/50 px-6 py-5 border-b border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{lang === 'fr' ? 'Supprimer toutes les compétences' : 'Delete all competencies'}</h3>
                  <p className="text-sm text-gray-500">{lang === 'fr' ? 'Action irréversible.' : 'This action cannot be undone.'}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600">
                {lang === 'fr'
                  ? <>Supprimer définitivement les <strong>{competencies.filter(cc => competencyFilteredSubjectSpecialtiesAll.some(sc => sc.id === cc.subject_specialty_id)).length} compétence(s)</strong> des <strong>{competencyFilteredSubjectSpecialtiesAll.length} matière(s)</strong> visibles dans les filtres actuels ?<br/><span className="text-xs text-gray-400">Astuce : filtrez par niveau/spécialité/matière avant pour cibler la suppression.</span></>
                  : <>Permanently delete <strong>{competencies.filter(cc => competencyFilteredSubjectSpecialtiesAll.some(sc => sc.id === cc.subject_specialty_id)).length} competency(ies)</strong> of the <strong>{competencyFilteredSubjectSpecialtiesAll.length} visible subject(s)</strong> ?</>}
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button onClick={() => setConfirmDeleteAllGlobal(false)} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">{txt.cancel}</button>
              <button onClick={handleDeleteAllCompetencies} disabled={deletingAllGlobal} className={cn('flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl', deletingAllGlobal ? 'bg-red-300 cursor-not-allowed' : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800')}>
                {deletingAllGlobal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {lang === 'fr' ? 'Tout supprimer' : 'Delete all'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DUPLICATE MODAL */}
      {duplicateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDuplicateModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Copy className="w-5 h-5 text-blue-600" />{lang === 'fr' ? 'Dupliquer les matieres' : 'Duplicate subjects'}</h3>
              <button onClick={() => setDuplicateModalOpen(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Specialite source' : 'Source specialty'}</label><select value={duplicateSourceClass} onChange={(e) => setDuplicateSourceClass(Number(e.target.value) || '')} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"><option value="">{txt.select}</option>{specialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}</select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Specialite cible' : 'Target specialty'}</label><select value={duplicateTargetClass} onChange={(e) => setDuplicateTargetClass(Number(e.target.value) || '')} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"><option value="">{txt.select}</option>{specialties.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}</select></div>
              <div className="flex items-center gap-2"><input type="checkbox" id="overwrite" checked={duplicateOverwrite} onChange={(e) => setDuplicateOverwrite(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600" /><label htmlFor="overwrite" className="text-sm text-gray-700">{lang === 'fr' ? 'Ecraser les existantes' : 'Overwrite existing'}</label></div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button onClick={() => setDuplicateModalOpen(false)} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">{txt.cancel}</button>
              <button onClick={handleDuplicate} disabled={isSubmitting} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">{isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}{lang === 'fr' ? 'Dupliquer' : 'Duplicate'}</button>
            </div>
          </div>
        </div>
      )}

      {/* AP ASSIGNMENT MODAL */}
      {apModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeApModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><UserCheck className="w-5 h-5 text-blue-600" />{lang === 'fr' ? 'Assigner un Animateur Pedagogique' : 'Assign Pedagogical Animator'}</h3>
              <button onClick={closeApModal} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Enseignant (AP)' : 'Teacher (AP)'}</label><select value={apTeacherId} onChange={(e) => setApTeacherId(Number(e.target.value) || '')} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"><option value="">{lang === 'fr' ? 'Aucun AP' : 'No AP'}</option>{teachers.map(t => (<option key={t.id} value={t.id}>{t.full_name || `${t.last_name} ${t.first_name}`}</option>))}</select></div>
              {teachers.length === 0 && (<p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{lang === 'fr' ? 'Aucun enseignant actif trouve' : 'No active teachers found'}</p>)}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button onClick={closeApModal} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">{txt.cancel}</button>
              <button onClick={handleAssignAP} disabled={isSubmitting} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">{isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}{lang === 'fr' ? 'Assigner' : 'Assign'}</button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteModalOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeDeleteModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-red-50 to-red-100/50 px-6 py-5 border-b border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
                <div><h3 className="text-lg font-semibold text-gray-900">{txt.confirmDeleteTitle}</h3><p className="text-sm text-gray-500">{txt.confirmDeleteWarning}</p></div>
              </div>
            </div>
            <div className="px-6 py-5"><p className="text-sm text-gray-600">{txt.confirmDeleteMessage} <span className="font-semibold">"{deleteTarget.name}"</span> ?</p></div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button onClick={closeDeleteModal} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">{txt.cancel}</button>
              <button onClick={handleConfirmDelete} disabled={isSubmitting} className={cn('flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl', isSubmitting ? 'bg-red-300 cursor-not-allowed' : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800')}>{isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}{txt.confirm}</button>
            </div>
          </div>
        </div>
      )}

      {/* GROUP MANAGEMENT MODAL */}
      {groupModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeGroupModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Layers className="w-5 h-5 text-purple-600" />{txt.manageGroups}</h3>
              <button onClick={closeGroupModal} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-center gap-2">
                <input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value.toUpperCase())} placeholder={txt.newGroup} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400" onKeyDown={(e) => { if (e.key === 'Enter') handleAddGroup() }} />
                <button onClick={handleAddGroup} className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl text-sm font-medium hover:from-purple-700 hover:to-purple-800 transition-all shadow-md shadow-purple-200"><Plus className="w-4 h-4" /></button>
              </div>
              <div className="space-y-2">
                {subjectGroups.length === 0 && (<p className="text-sm text-gray-400 text-center py-4">{lang === 'fr' ? 'Aucun groupe defini' : 'No groups defined'}</p>)}
                {subjectGroups.map((group) => (
                  <div key={group} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <span className="text-sm font-medium text-gray-700">{group}</span>
                    <button onClick={() => handleDeleteGroup(group)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title={txt.delete}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button onClick={closeGroupModal} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">{txt.close}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}